import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Userpage.css";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
    Mail,
    Phone,
    ShieldCheck,
    Wifi,
    Calendar,
    Clock,
    Zap,
    Check,
    Activity,
    ChevronRight,
    Tv,
    X,
    CheckCircle,
    Eye,
    EyeOff
} from "lucide-react";
import { FaEdit } from "react-icons/fa";

function UserDashboard() {

    const navigate = useNavigate();
    const location = useLocation();

    const username = sessionStorage.getItem("username");

    /* =========================================================
       USER
    ========================================================= */

    const [user, setUser] = useState({
        fullname: "",
        email: "",
        mobile: "",
        status: ""
    });

    const [showEditModal, setShowEditModal] = useState(false);
    const [showScheduledWarning, setShowScheduledWarning] = useState(false);
    const [updatingUser, setUpdatingUser] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [editForm, setEditForm] = useState({
        id: "",
        fullname: "",
        username: "",
        email: "",
        password: "",
        mobile: "",
        city: "",
        address: "",
        pincode: ""
    });

    /* =========================================================
       CURRENT PLAN
    ========================================================= */

    const [currentBroadbandPlan, setCurrentBroadbandPlan] = useState(null);
    const [showMobileSubDetails, setShowMobileSubDetails] = useState(false);

    /* =========================================================
       BROADBAND PAYMENT STATUS

       Source of truth for SUCCESS + PENDING broadband payments.
       This is loaded independently from paginated payment history
       so a pending broadband payment is not lost because of the
       selected month, search, or payment-history page.
    ========================================================= */

    const [broadbandPaymentRecords, setBroadbandPaymentRecords] =
        useState([]);

    const [broadbandPaymentStatusLoading, setBroadbandPaymentStatusLoading] =
        useState(true);

    /*
     * NEXT / SCHEDULED BROADBAND PLAN
     *
     * A successful broadband payment can be marked SCHEDULED.
     * Such a payment is already purchased and should be displayed
     * separately from the current ACTIVE plan.
     *
     * PENDING remains a separate state and means administrator
     * activation is still required.
     */
    const [upcomingBroadbandPlan, setUpcomingBroadbandPlan] = useState(null);
    const [upcomingPlanLoading, setUpcomingPlanLoading] = useState(false);

    const [loading, setLoading] = useState(true);

    /* =========================================================
       ACTIVE OTT ADD-ONS
    ========================================================= */

    const [activeAddons, setActiveAddons] = useState([]);
    const [activeAddonsLoading, setActiveAddonsLoading] = useState(true);

    /*
     * Source of truth for OTT add-on payment status.
     * This is loaded independently from paginated payment history.
     */
    const [addonPaymentRecords, setAddonPaymentRecords] = useState([]);
    const [addonPaymentStatusLoading, setAddonPaymentStatusLoading] =
        useState(true);

    /* =========================================================
       BROADBAND / OTT PLANS
    ========================================================= */

    const [plans, setPlans] = useState([]);
    const [selectedSpeed, setSelectedSpeed] = useState(null);

    /* Broadband OTT popup */
    const [activeOttModal, setActiveOttModal] = useState(null);

    /* Add-on OTT popup */
    const [activeAddonOttModal, setActiveAddonOttModal] =
        useState(null);

    /*
     * Payment confirmation
     *
     * When the user already has an active broadband plan, do not
     * navigate directly to the payment page. Store the selected
     * payment details and show a confirmation dialog first.
     */
    const [pendingPayment, setPendingPayment] = useState(null);

    const [ottPlatforms, setOttPlatforms] = useState([]);
    const [ottAddonPlans, setOttAddonPlans] = useState([]);

    /*
     * addonOtts structure:
     *
     * {
     *    1: [ott1, ott2, ott3],
     *    2: [ott1, ott2],
     *    3: [ott1, ott2]
     * }
     *
     * Key = OTT addon plan ID
     */
    const [addonOtts, setAddonOtts] = useState({});

    const [plansLoading, setPlansLoading] = useState(true);
    const [ottLoading, setOttLoading] = useState(true);
    const [addonLoading, setAddonLoading] = useState(true);

    /* =========================================================
       ACTIVE BROADBAND / OTT ADD-ON HELPERS
    ========================================================= */

    /* =========================================================
       INDIA TIMEZONE HELPERS

       Backend/MySQL business dates are stored as:
       YYYY-MM-DD HH:mm:ss

       Those values do not contain timezone information. Since the
       application uses Asia/Kolkata, parse timezone-less backend
       date/time values explicitly as IST instead of allowing the
       browser to guess the timezone.
    ========================================================= */

    const INDIA_TIMEZONE = "Asia/Kolkata";
    const INDIA_OFFSET_MINUTES = 330;

    const getIndiaDateParts = (date = new Date()) => {
        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone: INDIA_TIMEZONE,
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        }).formatToParts(date);

        const result = {};

        parts.forEach((part) => {
            if (part.type !== "literal") {
                result[part.type] = part.value;
            }
        });

        return result;
    };

    const getCurrentIndiaMonth = () => {
        const parts = getIndiaDateParts(new Date());
        return `${parts.year}-${parts.month}`;
    };

    /*
     * Parse a backend MySQL DATETIME as Asia/Kolkata.
     * If the API returns an explicit timezone (Z or +/-HH:mm),
     * that timezone is respected instead.
     */
    const parseIndiaDateTime = (value) => {
        if (!value) return null;

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        const raw = String(value).trim();

        if (!raw) return null;

        if (/(?:Z|[+-]\d{2}:\d{2})$/i.test(raw)) {
            const explicitDate = new Date(raw);
            return Number.isNaN(explicitDate.getTime())
                ? null
                : explicitDate;
        }

        const match = raw.match(
            /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/
        );

        if (match) {
            const year = Number(match[1]);
            const month = Number(match[2]);
            const day = Number(match[3]);
            const hour = Number(match[4] || 0);
            const minute = Number(match[5] || 0);
            const second = Number(match[6] || 0);
            const milliseconds = Number(
                (match[7] || "0").padEnd(3, "0").slice(0, 3)
            );

            const utcMillis =
                Date.UTC(
                    year,
                    month - 1,
                    day,
                    hour,
                    minute,
                    second,
                    milliseconds
                ) -
                INDIA_OFFSET_MINUTES * 60 * 1000;

            const parsedDate = new Date(utcMillis);

            return Number.isNaN(parsedDate.getTime())
                ? null
                : parsedDate;
        }

        const fallbackDate = new Date(raw);

        return Number.isNaN(fallbackDate.getTime())
            ? null
            : fallbackDate;
    };

    /*
     * Plan dates are date-oriented values. When the backend returns
     * YYYY-MM-DD or YYYY-MM-DD HH:mm:ss, preserve the database date
     * instead of allowing JavaScript timezone conversion to shift it.
     */
    const formatPlanDate = (value) => {
        if (!value) return "-";

        const strVal = String(value).trim();

        const datePartMatch = strVal.match(
            /^(\d{4})-(\d{2})-(\d{2})/
        );

        if (datePartMatch) {
            return `${datePartMatch[1]}-${datePartMatch[2]}-${datePartMatch[3]}`;
        }

        const date = parseIndiaDateTime(value);

        if (!date) return value;

        return new Intl.DateTimeFormat("en-CA", {
            timeZone: INDIA_TIMEZONE,
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        }).format(date);
    };

    /*
     * Payment transaction timestamps are displayed explicitly in IST.
     */
    const formatIndiaDateTime = (value) => {
        if (!value) return "-";

        const date = parseIndiaDateTime(value);

        if (!date) return value;

        return new Intl.DateTimeFormat("en-IN", {
            timeZone: INDIA_TIMEZONE,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true
        }).format(date);
    };

    const hasActiveBroadbandPlan = () => {
        if (!currentBroadbandPlan) return false;

        const planType = String(
            currentBroadbandPlan.plan_type ||
            ""
        ).trim().toUpperCase();

        if (planType !== "BROADBAND") return false;

        const status = String(
            currentBroadbandPlan.plan_status ||
            currentBroadbandPlan.status ||
            ""
        ).trim().toUpperCase();

        if (status !== "ACTIVE") return false;

        if (currentBroadbandPlan.plan_expiry_date) {
            const expiry = parseIndiaDateTime(
                currentBroadbandPlan.plan_expiry_date
            );

            if (
                expiry &&
                expiry.getTime() < Date.now()
            ) {
                return false;
            }
        }

        return true;
    };

    /*
     * =========================================================
     * SCHEDULED / UPCOMING BROADBAND PLAN
     * =========================================================
     *
     * A successful broadband payment can have:
     *
     *     payment_status = SUCCESS
     *     plan_status    = SCHEDULED
     *     plan_type      = BROADBAND
     *
     * This means the plan has already been purchased and is
     * scheduled to start after/based on its plan_start_date.
     *
     * This is different from:
     *
     *     payment_status = SUCCESS
     *     plan_status    = PENDING
     *
     * PENDING means administrator activation is still required.
     */
    const hasUpcomingBroadbandPlan = () => {
        if (!upcomingBroadbandPlan) {
            return false;
        }

        const planType = String(
            upcomingBroadbandPlan.plan_type || ""
        )
            .trim()
            .toUpperCase();

        const planStatus = String(
            upcomingBroadbandPlan.plan_status ||
            upcomingBroadbandPlan.status ||
            ""
        )
            .trim()
            .toUpperCase();

        const paymentStatus = String(
            upcomingBroadbandPlan.payment_status || ""
        )
            .trim()
            .toUpperCase();

        return (
            planType === "BROADBAND" &&
            planStatus === "SCHEDULED" &&
            paymentStatus === "SUCCESS"
        );
    };

    const getContinuationMessage = () => {
        if (!upcomingBroadbandPlan) {
            return "";
        }

        return `Your scheduled broadband plan will start on ${formatPlanDate(
            upcomingBroadbandPlan.plan_start_date
        )}.`;
    };

    const showNoActivePlanNotification = () => {
        toast.warning(
            "There is no active broadband plan. Please buy an active broadband plan first.",
            {
                position: "top-right",
                autoClose: 3500
            }
        );
    };

    /*
     * Navigate to payment.
     *
     * Existing functionality is preserved:
     * - If there is no active broadband plan, go directly to payment.
     * - If there is an active broadband plan, show a confirmation box
     *   before going to payment.
     */
    const goToPayment = (paymentState) => {
        /*
         * If the user already has a SCHEDULED (upcoming) broadband plan,
         * block the purchase and show an informational popup instead.
         */
        if (hasUpcomingBroadbandPlan()) {
            setShowScheduledWarning(true);
            return;
        }

        if (hasActiveBroadbandPlan()) {
            setPendingPayment(paymentState);
            return;
        }

        navigate("/payment", {
            state: paymentState
        });
    };

    const confirmPendingPayment = () => {
        if (!pendingPayment) {
            return;
        }

        const paymentState = pendingPayment;

        setPendingPayment(null);

        navigate("/payment", {
            state: paymentState
        });
    };

    const cancelPendingPayment = () => {
        setPendingPayment(null);
    };

    /* =========================================================
       OTT ADD-ON STATUS HELPERS

       The add-on card follows the same visual/status behavior as
       the broadband card:

       ACTIVE:
         activeAddons contains addon_plan_id matching addon.id
         and the add-on is still within its expiry date.

       PENDING:
         payment_status = SUCCESS
         plan_status    = PENDING
         plan_type      = OTT_ADDON
         and addon_plan_id matches addon.id.

       IMPORTANT:
       addon_plan_id is the OTT add-on plan ID. Do not compare it
       with payments.id.
    ========================================================= */

    /*
     * =========================================================
     * OTT ADD-ON PLAN ID / TYPE HELPERS
     * =========================================================
     *
     * Broadband and OTT add-ons are stored in the SAME payments
     * table. The important difference is:
     *
     *     BROADBAND -> plan_type = BROADBAND
     *     ADD-ON    -> plan_type = ADDON
     *
     * For add-ons, addon_plan_id is the ID of the row from
     * get_ott_addon_plans.php.
     *
     * IMPORTANT:
     *     payments.id is NOT addon_plan_id.
     */

    const normalizePlanType = (value) => {
        return String(value || "")
            .trim()
            .toUpperCase()
            .replace(/[\s-]+/g, "_");
    };

    const getAddonPlanId = (addon) => {
        if (!addon) {
            return 0;
        }

        /*
         * The add-on plans API normally returns id.
         * Keep the other fields as fallbacks so the UI remains
         * compatible with older API responses.
         */
        const possibleIds = [
            addon.id,
            addon.addon_plan_id,
            addon.ott_addon_plan_id,
            addon.plan_id
        ];

        for (const value of possibleIds) {
            const id = Number(value);

            if (
                Number.isInteger(id) &&
                id > 0
            ) {
                return id;
            }
        }

        return 0;
    };

    const getPaymentAddonPlanId = (payment) => {
        if (!payment) {
            return 0;
        }

        const possibleIds = [
            payment.addon_plan_id,
            payment.ott_addon_plan_id,
            payment.plan_id
        ];

        for (const value of possibleIds) {
            const id = Number(value);

            if (
                Number.isInteger(id) &&
                id > 0
            ) {
                return id;
            }
        }

        return 0;
    };

    const isOttAddonPayment = (payment) => {
        if (!payment) {
            return false;
        }

        const planType = normalizePlanType(
            payment.plan_type ||
            payment.payment_type
        );

        /*
         * Database screenshot shows ADDON.
         * Keep OTT_ADDON/OTT ADDON as compatibility values.
         */
        return (
            planType === "ADDON" ||
            planType === "OTT_ADDON"
        );
    };

    const getAddonPaymentRecord = (
        addon,
        allowedStatuses = []
    ) => {

        if (!addon) {
            return null;
        }

        const addonPlanId =
            getAddonPlanId(addon);

        if (!addonPlanId) {
            return null;
        }

        const statusSet = new Set(
            allowedStatuses.map(
                (status) =>
                    String(status)
                        .trim()
                        .toUpperCase()
            )
        );

        /*
         * PRIMARY SOURCE:
         * dedicated, non-paginated add-on payment records.
         *
         * Fallback:
         * existing paymentHistory.
         */
        const sourceRecords =
            Array.isArray(addonPaymentRecords) &&
                addonPaymentRecords.length > 0
                ? addonPaymentRecords
                : (
                    Array.isArray(paymentHistory)
                        ? paymentHistory
                        : []
                );

        const matches =
            sourceRecords.filter(
                (payment) => {

                    if (
                        !isOttAddonPayment(
                            payment
                        )
                    ) {
                        return false;
                    }

                    const paymentAddonPlanId =
                        getPaymentAddonPlanId(
                            payment
                        );

                    if (
                        paymentAddonPlanId !==
                        addonPlanId
                    ) {
                        return false;
                    }

                    const paymentStatus =
                        String(
                            payment.payment_status ||
                            ""
                        )
                            .trim()
                            .toUpperCase();

                    const planStatus =
                        String(
                            payment.plan_status ||
                            ""
                        )
                            .trim()
                            .toUpperCase();

                    if (
                        paymentStatus !==
                        "SUCCESS"
                    ) {
                        return false;
                    }

                    if (
                        statusSet.size > 0 &&
                        !statusSet.has(
                            planStatus
                        )
                    ) {
                        return false;
                    }

                    return true;
                }
            );

        if (matches.length === 0) {
            return null;
        }

        return [...matches].sort(
            (a, b) => {

                const aTime =
                    parseIndiaDateTime(
                        a.transaction_date
                    )?.getTime() ||
                    Number(a.id || 0);

                const bTime =
                    parseIndiaDateTime(
                        b.transaction_date
                    )?.getTime() ||
                    Number(b.id || 0);

                return bTime - aTime;
            }
        )[0];
    };

    /*
     * =========================================================
     * ACTIVE ADD-ON
     * =========================================================
     *
     * First check getActiveAddons.php.
     * Then use paymentHistory as a safe fallback.
     *
     * Both checks require plan_type = ADDON/OTT_ADDON.
     */
    const isAddonActive = (addon) => {

        if (!addon) {
            return false;
        }

        const addonPlanId =
            getAddonPlanId(addon);

        if (!addonPlanId) {
            return false;
        }

        /*
         * -----------------------------------------------------
         * 1. ACTIVE ADD-ONS API
         * -----------------------------------------------------
         */
        if (
            Array.isArray(activeAddons) &&
            activeAddons.length > 0
        ) {

            const activeMatch =
                activeAddons.some(
                    (activeAddon) => {

                        const activePlanType =
                            normalizePlanType(
                                activeAddon.plan_type
                            );

                        /*
                         * Do not accidentally use a broadband
                         * payment as an OTT add-on.
                         */
                        if (
                            activePlanType &&
                            activePlanType !==
                            "ADDON" &&
                            activePlanType !==
                            "OTT_ADDON"
                        ) {
                            return false;
                        }

                        const activeAddonPlanId =
                            getPaymentAddonPlanId(
                                activeAddon
                            );

                        const status =
                            String(
                                activeAddon.plan_status ||
                                activeAddon.status ||
                                ""
                            )
                                .trim()
                                .toUpperCase();

                        if (
                            activeAddonPlanId !==
                            addonPlanId ||
                            status !==
                            "ACTIVE"
                        ) {
                            return false;
                        }

                        const expiryValue =
                            activeAddon.plan_expiry_date ||
                            activeAddon.expiry_date;

                        if (expiryValue) {

                            const expiry =
                                parseIndiaDateTime(
                                    expiryValue
                                );

                            if (
                                expiry &&
                                expiry.getTime() <=
                                Date.now()
                            ) {
                                return false;
                            }
                        }

                        return true;
                    }
                );

            if (activeMatch) {
                return true;
            }
        }

        /*
         * -----------------------------------------------------
         * 2. PAYMENT HISTORY FALLBACK
         * -----------------------------------------------------
         *
         * This is useful when getActiveAddons.php is delayed,
         * cached, or temporarily returns an empty response.
         */
        const activePayment =
            getAddonPaymentRecord(
                addon,
                ["ACTIVE"]
            );

        if (!activePayment) {
            return false;
        }

        const expiryValue =
            activePayment.plan_expiry_date;

        if (expiryValue) {

            const expiry =
                parseIndiaDateTime(
                    expiryValue
                );

            if (
                expiry &&
                expiry.getTime() <=
                Date.now()
            ) {
                return false;
            }
        }

        return true;
    };

    /*
     * =========================================================
     * PENDING ADD-ON PAYMENT
     * =========================================================
     *
     * Your DB screenshot contains:
     *
     *     plan_type      = ADDON
     *     addon_plan_id  = 1
     *     payment_status = SUCCESS
     *     plan_status    = PENDING
     *
     * Therefore this payment MUST make the corresponding
     * add-on card display:
     *
     *     - pending border
     *     - pending ribbon
     *     - PENDING ACTIVATION
     *     - disabled Activation Pending button
     */
    const isPendingAddonPayment = (payment) => {

        if (!payment) {
            return false;
        }

        const paymentStatus =
            String(
                payment.payment_status ||
                ""
            )
                .trim()
                .toUpperCase();

        const planStatus =
            String(
                payment.plan_status ||
                ""
            )
                .trim()
                .toUpperCase();

        return (
            isOttAddonPayment(payment) &&
            paymentStatus ===
            "SUCCESS" &&
            planStatus ===
            "PENDING" &&
            getPaymentAddonPlanId(
                payment
            ) > 0
        );
    };

    const getPendingAddonPayment = (
        addon
    ) => {

        const addonPlanId =
            getAddonPlanId(addon);

        if (!addonPlanId) {
            return null;
        }

        /*
         * IMPORTANT:
         *
         * Do NOT use paymentHistory here.
         *
         * paymentHistory is paginated and filtered by month/search.
         * Your OTT add-on payment status comes from:
         *
         *     getUserAddonPaymentStatuses.php
         *
         * and is stored in:
         *
         *     addonPaymentRecords
         *
         * Therefore the add-on card MUST use addonPaymentRecords.
         */
        const sourceRecords =
            Array.isArray(addonPaymentRecords)
                ? addonPaymentRecords
                : [];

        if (sourceRecords.length === 0) {
            return null;
        }

        const matches =
            sourceRecords.filter(
                (payment) => {

                    if (
                        !isPendingAddonPayment(
                            payment
                        )
                    ) {
                        return false;
                    }

                    const paymentAddonPlanId =
                        getPaymentAddonPlanId(
                            payment
                        );

                    /*
                     * Match:
                     *
                     * ott_addon_plans.id
                     *       =
                     * payments.addon_plan_id
                     */
                    return (
                        paymentAddonPlanId ===
                        addonPlanId
                    );
                }
            );

        if (
            matches.length === 0
        ) {
            return null;
        }

        /*
         * If multiple pending payments exist for the same
         * add-on, use the latest one.
         */
        return [...matches].sort(
            (a, b) => {

                const aTime =
                    parseIndiaDateTime(
                        a.transaction_date
                    )?.getTime() ||
                    Number(a.id || 0);

                const bTime =
                    parseIndiaDateTime(
                        b.transaction_date
                    )?.getTime() ||
                    Number(b.id || 0);

                return bTime - aTime;
            }
        )[0];
    };

    /*
     * This helper is useful for the card render.
     *
     * It intentionally checks only the dedicated add-on payment
     * endpoint, so a paginated payment-history response can never
     * make the card lose its pending state.
     */
    const hasPendingAddonPayment = (
        addon
    ) => {
        return Boolean(
            getPendingAddonPayment(addon)
        );
    };

    const chooseAddon = (addon) => {

        if (
            isAddonActive(addon)
        ) {

            toast.info(
                "This OTT add-on is already active.",
                {
                    position:
                        "top-right",
                    autoClose: 3000
                }
            );

            return;
        }

        const pendingAddonPayment =
            getPendingAddonPayment(
                addon
            );

        if (
            pendingAddonPayment
        ) {

            toast.info(
                "This OTT add-on payment is completed and activation is pending.",
                {
                    position:
                        "top-right",
                    autoClose: 3500
                }
            );

            return;
        }

        if (
            !hasActiveBroadbandPlan()
        ) {

            showNoActivePlanNotification();

            return;
        }

        goToPayment({
            plan: addon,

            duration: {
                months:
                    addon.duration_months,

                duration_months:
                    addon.duration_months,

                duration_label:
                    addon.duration_label,

                price:
                    addon.price
            },

            /*
             * Payment page can use OTT_ADDON,
             * but the database payment record is
             * normalized as ADDON. The user page accepts
             * both values.
             */
            planType:
                "OTT_ADDON",

            addonPlan:
                addon,

            selectedAddon:
                addon,

            hasActiveBroadband:
                true
        });
    };


    const fetchActiveAddons = useCallback(async () => {
        if (!username) {
            setActiveAddons([]);
            setActiveAddonsLoading(false);
            return;
        }

        try {
            setActiveAddonsLoading(true);

            const res = await axios.get(
                `https://ritubroadband.in/api/getActiveAddons.php?username=${encodeURIComponent(
                    username
                )}`,
                {
                    headers: {
                        Accept: "application/json"
                    }
                }
            );

            console.log(
                "Active OTT Add-ons Response:",
                res.data
            );

            const apiStatus = String(
                res.data?.status || ""
            )
                .trim()
                .toLowerCase();

            if (apiStatus === "success") {
                const addons = Array.isArray(res.data?.data)
                    ? res.data.data
                    : [];

                const normalizedAddons = addons.map(
                    (addon) => ({
                        ...addon,

                        /*
                         * IMPORTANT:
                         * Do NOT use payments.id as addon_plan_id.
                         * payments.id identifies the payment row.
                         */
                        addon_plan_id: Number(
                            addon.addon_plan_id ??
                            addon.ott_addon_plan_id ??
                            addon.plan_id ??
                            0
                        ),

                        plan_status: String(
                            addon.plan_status ||
                            addon.status ||
                            ""
                        )
                            .trim()
                            .toUpperCase()
                    })
                );

                console.log(
                    "Active OTT Add-ons:",
                    normalizedAddons
                );

                setActiveAddons(
                    normalizedAddons
                );
            } else {
                console.warn(
                    "Active OTT Add-ons API returned non-success:",
                    res.data
                );

                setActiveAddons([]);
            }
        } catch (error) {
            console.error(
                "Active OTT add-ons error:",
                error
            );

            setActiveAddons([]);
        } finally {
            setActiveAddonsLoading(false);
        }
    }, [username]);

    useEffect(() => {
        fetchActiveAddons();
    }, [fetchActiveAddons]);

    /*
     * =========================================================
     * OTT ADD-ON PAYMENT STATUS
     * =========================================================
     *
     * Do not use getPaymentHistory.php for the card status because
     * that API is paginated and can be filtered by month/search.
     *
     * This endpoint reads the same payments table and returns all
     * ADDON records for this user.
     */
    const fetchAddonPaymentStatuses = useCallback(async () => {
        if (!username) {
            setAddonPaymentRecords([]);
            setAddonPaymentStatusLoading(false);
            return;
        }

        try {
            setAddonPaymentStatusLoading(true);

            const res = await axios.get(
                `https://ritubroadband.in/api/getUserAddonPaymentStatuses.php?username=${encodeURIComponent(
                    username
                )}`,
                {
                    headers: {
                        Accept: "application/json"
                    }
                }
            );

            console.log(
                "OTT ADD-ON PAYMENT STATUS RESPONSE:",
                res.data
            );

            if (
                String(res.data?.status || "")
                    .trim()
                    .toLowerCase() === "success"
            ) {
                const records = Array.isArray(res.data?.data)
                    ? res.data.data
                    : [];

                const normalizedRecords = records.map(
                    (payment) => ({
                        ...payment,
                        id: Number(payment.id || 0),
                        addon_plan_id: Number(
                            payment.addon_plan_id || 0
                        ),
                        plan_type: normalizePlanType(
                            payment.plan_type
                        ),
                        payment_status: String(
                            payment.payment_status || ""
                        )
                            .trim()
                            .toUpperCase(),
                        plan_status: String(
                            payment.plan_status || ""
                        )
                            .trim()
                            .toUpperCase()
                    })
                );

                console.log(
                    "NORMALIZED OTT ADD-ON PAYMENT RECORDS:",
                    normalizedRecords
                );

                setAddonPaymentRecords(
                    normalizedRecords
                );
            } else {
                setAddonPaymentRecords([]);
            }
        } catch (error) {
            console.error(
                "OTT add-on payment status error:",
                error
            );
            setAddonPaymentRecords([]);
        } finally {
            setAddonPaymentStatusLoading(false);
        }
    }, [username]);

    useEffect(() => {
        fetchAddonPaymentStatuses();
    }, [fetchAddonPaymentStatuses]);

    /* =========================================================
       PAYMENT HISTORY
    ========================================================= */

    const [paymentHistory, setPaymentHistory] = useState([]);
    const [paymentHistoryLoading, setPaymentHistoryLoading] =
        useState(true);

    const [paymentPage, setPaymentPage] = useState(1);
    const [paymentTotalPages, setPaymentTotalPages] = useState(1);
    const [paymentTotal, setPaymentTotal] = useState(0);

    const [paymentSearch, setPaymentSearch] = useState("");

    const currentMonth = getCurrentIndiaMonth();

    const [paymentMonth, setPaymentMonth] =
        useState(currentMonth);

    /* =========================================================
       USER DETAILS
    ========================================================= */

    useEffect(() => {

        setUser({
            fullname:
                sessionStorage.getItem("fullname") || "",

            email:
                sessionStorage.getItem("email") || "",

            mobile:
                sessionStorage.getItem("mobile") || "",

            status:
                sessionStorage.getItem("status") || ""
        });

    }, []);

    const handleOpenEditModal = async () => {
        try {
            setUpdatingUser(true);
            const response = await axios.get(`https://ritubroadband.in/api/get_users.php?search=${encodeURIComponent(username)}`);
            if (response.data.status === "success" && response.data.data.length > 0) {
                const matchedUser = response.data.data.find(u => u.username === username);
                if (matchedUser) {
                    setEditForm({
                        id: matchedUser.id,
                        fullname: matchedUser.fullname || "",
                        username: matchedUser.username || "",
                        email: matchedUser.email || "",
                        password: "",
                        mobile: matchedUser.mobile || "",
                        city: matchedUser.city || "",
                        address: matchedUser.address || "",
                        pincode: matchedUser.pincode || ""
                    });
                    setShowEditModal(true);
                } else {
                    toast.error("User details not found");
                }
            } else {
                toast.error("Failed to fetch user details");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred fetching details");
        } finally {
            setUpdatingUser(false);
        }
    };

    const handleEditChange = (e) => {
        setEditForm({ ...editForm, [e.target.name]: e.target.value });
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();

        if (!editForm.fullname.trim()) {
            toast.error("Full name is required");
            return;
        }

        if (editForm.password && editForm.password.trim().length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }

        setUpdatingUser(true);

        try {
            const response = await axios.post("https://ritubroadband.in/api/update_user.php", {
                id: editForm.id,
                fullname: editForm.fullname.trim(),
                username: editForm.username.trim(),
                email: editForm.email.trim(),
                password: editForm.password.trim(),
                mobile: editForm.mobile.trim(),
                city: editForm.city.trim(),
                address: editForm.address.trim(),
                pincode: editForm.pincode.trim()
            });

            if (response.data.status === "success") {
                toast.success("Details updated successfully");
                
                sessionStorage.setItem("fullname", editForm.fullname.trim());
                
                setUser(prev => ({
                    ...prev,
                    fullname: editForm.fullname.trim(),
                }));

                setShowEditModal(false);
            } else {
                toast.error(response.data.message || "Failed to update details");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to update details. Please try again.");
        } finally {
            setUpdatingUser(false);
        }
    };

    /* =========================================================
       CURRENT ACTIVE BROADBAND PLAN

       PAYMENT FLOW:
       - getCurrentPlan.php returns the genuinely ACTIVE plan.
       - SCHEDULED broadband payments are read from
         getUserBroadbandPaymentStatuses.php.
       - PENDING broadband payments remain separate and require
         administrator activation.
    ========================================================= */

    /* =========================================================
       BROADBAND PAYMENT STATUS
       =========================================================

       IMPORTANT:
       Do not use getPaymentHistory.php to determine pending
       broadband activation. Payment history is paginated and can
       be filtered by month/search.

       This endpoint is the dedicated source of truth for broadband
       payment records and returns SUCCESS broadband payments,
       including plan_status = PENDING.
    ========================================================= */

    const fetchBroadbandPaymentStatuses = useCallback(async () => {
        if (!username) {
            setBroadbandPaymentRecords([]);
            setBroadbandPaymentStatusLoading(false);
            return;
        }

        try {
            setBroadbandPaymentStatusLoading(true);

            const res = await axios.get(
                `https://ritubroadband.in/api/getUserBroadbandPaymentStatuses.php?username=${encodeURIComponent(
                    username
                )}`,
                {
                    headers: {
                        Accept: "application/json"
                    }
                }
            );

            console.log(
                "BROADBAND PAYMENT STATUS RESPONSE:",
                res.data
            );

            const apiStatus = String(
                res.data?.status ||
                (res.data?.success === true ? "success" : "")
            )
                .trim()
                .toLowerCase();

            if (apiStatus === "success") {
                const records = Array.isArray(res.data?.data)
                    ? res.data.data
                    : [];

                const normalizedRecords = records.map((payment) => ({
                    ...payment,
                    id: Number(payment.id || 0),
                    plan_type: String(
                        payment.plan_type || ""
                    )
                        .trim()
                        .toUpperCase(),
                    payment_status: String(
                        payment.payment_status || ""
                    )
                        .trim()
                        .toUpperCase(),
                    plan_status: String(
                        payment.plan_status || ""
                    )
                        .trim()
                        .toUpperCase(),
                    amount: Number(payment.amount || 0),
                    duration_months: Number(
                        payment.duration_months || payment.months || 0
                    )
                }));

                console.log(
                    "NORMALIZED BROADBAND PAYMENT RECORDS:",
                    normalizedRecords
                );

                setBroadbandPaymentRecords(
                    normalizedRecords.filter(
                        (payment) => payment.plan_type === "BROADBAND"
                    )
                );
            } else {
                setBroadbandPaymentRecords([]);
            }
        } catch (error) {
            console.error(
                "Broadband payment status error:",
                error
            );
            setBroadbandPaymentRecords([]);
        } finally {
            setBroadbandPaymentStatusLoading(false);
        }
    }, [username]);

    useEffect(() => {
        fetchBroadbandPaymentStatuses();
    }, [fetchBroadbandPaymentStatuses]);

    const fetchCurrentBroadbandPlan = useCallback(async () => {
        if (!username) {
            setCurrentBroadbandPlan(null);
            setUpcomingBroadbandPlan(null);
            setLoading(false);
            setUpcomingPlanLoading(false);
            return;
        }

        setLoading(true);
        setUpcomingPlanLoading(true);

        try {
            const currentUrl =
                `https://ritubroadband.in/api/getCurrentPlan.php?username=${encodeURIComponent(
                    username
                )}`;

            const currentResponse = await axios.get(
                currentUrl,
                {
                    headers: {
                        Accept: "application/json"
                    }
                }
            );

            console.log(
                "CURRENT PLAN RESPONSE:",
                currentResponse.data
            );

            const response = currentResponse.data;

            const apiStatus = String(
                response?.status ||
                (response?.success === true
                    ? "success"
                    : "")
            )
                .trim()
                .toLowerCase();

            /*
             * =====================================================
             * CURRENT ACTIVE PLAN
             * =====================================================
             *
             * getCurrentPlan.php is the source of truth for the
             * currently ACTIVE broadband plan.
             */
            if (
                apiStatus === "success" &&
                response?.data
            ) {
                const currentPlan = response.data;

                const planType = String(
                    currentPlan.plan_type || ""
                )
                    .trim()
                    .toUpperCase();

                const planStatus = String(
                    currentPlan.plan_status || ""
                )
                    .trim()
                    .toUpperCase();

                if (
                    planType === "BROADBAND" &&
                    planStatus === "ACTIVE"
                ) {
                    setCurrentBroadbandPlan(
                        currentPlan
                    );
                } else {
                    setCurrentBroadbandPlan(null);
                }
            } else {
                setCurrentBroadbandPlan(null);
            }

            /*
             * =====================================================
             * SCHEDULED BROADBAND PLAN
             * =====================================================
             *
             * Do not use the paginated payment-history API here.
             *
             * broadbandPaymentRecords comes from:
             *
             * getUserBroadbandPaymentStatuses.php
             *
             * and contains the SUCCESS broadband payment records,
             * including plan_status = SCHEDULED.
             */
            if (
                Array.isArray(broadbandPaymentRecords)
            ) {
                const scheduledPlans =
                    broadbandPaymentRecords
                        .filter((payment) => {

                            const planType = String(
                                payment.plan_type || ""
                            )
                                .trim()
                                .toUpperCase();

                            const planStatus = String(
                                payment.plan_status ||
                                payment.status ||
                                ""
                            )
                                .trim()
                                .toUpperCase();

                            const paymentStatus = String(
                                payment.payment_status || ""
                            )
                                .trim()
                                .toUpperCase();

                            return (
                                planType === "BROADBAND" &&
                                planStatus === "SCHEDULED" &&
                                paymentStatus === "SUCCESS"
                            );
                        })
                        .sort((a, b) => {

                            const aTime =
                                parseIndiaDateTime(
                                    a.plan_start_date
                                )?.getTime() || 0;

                            const bTime =
                                parseIndiaDateTime(
                                    b.plan_start_date
                                )?.getTime() || 0;

                            return aTime - bTime;
                        });

                /*
                 * Show the nearest scheduled broadband plan.
                 */
                setUpcomingBroadbandPlan(
                    scheduledPlans.length > 0
                        ? scheduledPlans[0]
                        : null
                );

                console.log(
                    "SCHEDULED BROADBAND PLANS:",
                    scheduledPlans
                );
            } else {
                setUpcomingBroadbandPlan(null);
            }

        } catch (error) {
            console.error(
                "Current broadband plan loading error:",
                error
            );

            setCurrentBroadbandPlan(null);
            setUpcomingBroadbandPlan(null);

        } finally {
            setLoading(false);
            setUpcomingPlanLoading(false);
        }
    }, [
        username,
        broadbandPaymentRecords
    ]);

    useEffect(() => {
        fetchCurrentBroadbandPlan();
    }, [fetchCurrentBroadbandPlan]);

    /* =========================================================
       PAYMENT HISTORY
    ========================================================= */

    const fetchPaymentHistory = useCallback(async () => {
        if (!username) {
            setPaymentHistoryLoading(false);
            return;
        }

        try {
            setPaymentHistoryLoading(true);

            const params = new URLSearchParams();

            params.append(
                "username",
                username
            );

            params.append(
                "page",
                paymentPage
            );

            if (paymentSearch.trim()) {
                params.append(
                    "search",
                    paymentSearch.trim()
                );
            }

            if (paymentMonth) {
                params.append(
                    "month",
                    paymentMonth
                );
            }

            const res = await axios.get(
                `https://ritubroadband.in/api/getPaymentHistory.php?${params.toString()}`
            );

            console.log(
                "Payment History Response:",
                res.data
            );

            if (
                res.data.status ===
                "success"
            ) {
                setPaymentHistory(
                    res.data.data || []
                );

                setPaymentTotal(
                    res.data.total || 0
                );

                setPaymentTotalPages(
                    res.data.totalPages || 1
                );
            } else {
                setPaymentHistory([]);
                setPaymentTotal(0);
                setPaymentTotalPages(1);
            }
        } catch (error) {
            console.error(
                "Payment history error:",
                error
            );

            setPaymentHistory([]);
            setPaymentTotal(0);
            setPaymentTotalPages(1);
        } finally {
            setPaymentHistoryLoading(false);
        }
    }, [
        username,
        paymentPage,
        paymentSearch,
        paymentMonth
    ]);

    useEffect(() => {
        fetchPaymentHistory();
    }, [fetchPaymentHistory]);

    /* =========================================================
       RELOAD ALL USER DATA (ON PAYMENT SUCCESS OR ROUTE RETURN)
    ========================================================= */

    const reloadAllUserData = useCallback(async () => {
        if (!username) return;

        await Promise.allSettled([
            fetchCurrentBroadbandPlan(),
            fetchBroadbandPaymentStatuses(),
            fetchActiveAddons(),
            fetchAddonPaymentStatuses(),
            fetchPaymentHistory()
        ]);
    }, [
        username,
        fetchCurrentBroadbandPlan,
        fetchBroadbandPaymentStatuses,
        fetchActiveAddons,
        fetchAddonPaymentStatuses,
        fetchPaymentHistory
    ]);

    useEffect(() => {
        if (location.state?.paymentSuccess) {
            toast.success(
                "Payment completed successfully! Subscription and payment details updated."
            );
            reloadAllUserData();
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, reloadAllUserData, navigate, location.pathname]);

    /* =========================================================
       LOAD BROADBAND OTT ASSIGNMENTS
    ========================================================= */

    const loadOttAssignments = async (
        broadbandPlans
    ) => {

        if (
            !Array.isArray(broadbandPlans) ||
            broadbandPlans.length === 0
        ) {

            return broadbandPlans;

        }

        const durationRequests = [];

        broadbandPlans.forEach((plan) => {

            const durations =
                Array.isArray(plan.durations)
                    ? plan.durations
                    : [];

            durations.forEach((duration) => {

                if (duration?.id) {

                    durationRequests.push({
                        planId: plan.id,
                        durationId: duration.id
                    });

                }

            });

        });

        if (durationRequests.length === 0) {

            return broadbandPlans;

        }

        const assignmentResults =
            await Promise.all(

                durationRequests.map(
                    async ({
                        durationId
                    }) => {

                        try {

                            const res =
                                await axios.get(
                                    `https://ritubroadband.in/api/get_plan_ott_assignments.php?plan_duration_id=${durationId}`
                                );

                            if (
                                res.data.status !==
                                "success"
                            ) {

                                return {
                                    durationId,
                                    otts: []
                                };

                            }

                            const assignedOtts =
                                Array.isArray(
                                    res.data.data
                                )
                                    ? res.data.data
                                        .filter(
                                            (ott) =>
                                                Number(
                                                    ott.assigned
                                                ) === 1
                                        )
                                        .filter(
                                            (ott) =>
                                                Number(
                                                    ott.active
                                                ) !== 0
                                        )
                                        .map(
                                            (ott) => {

                                                return {
                                                    ...ott,

                                                    ott_name:
                                                        ott.ott_name ||
                                                        ott.name ||
                                                        "",

                                                    logo:
                                                        ott.logo ||
                                                        ""
                                                };

                                            }
                                        )
                                    : [];

                            return {
                                durationId,
                                otts:
                                    assignedOtts
                            };

                        } catch (error) {

                            console.error(
                                `OTT assignment error for duration ${durationId}:`,
                                error
                            );

                            return {
                                durationId,
                                otts: []
                            };

                        }

                    }
                )

            );

        const ottByDurationId = {};

        assignmentResults.forEach(
            ({
                durationId,
                otts
            }) => {

                ottByDurationId[
                    Number(durationId)
                ] = otts;

            }
        );

        const updatedPlans =
            broadbandPlans.map(
                (plan) => {

                    const durations =
                        Array.isArray(
                            plan.durations
                        )
                            ? plan.durations
                            : [];

                    const updatedDurations =
                        durations.map(
                            (duration) => {

                                return {
                                    ...duration,

                                    ott_platforms:
                                        ottByDurationId[
                                        Number(
                                            duration.id
                                        )
                                        ] || []
                                };

                            }
                        );

                    return {
                        ...plan,

                        durations:
                            updatedDurations
                    };

                }
            );

        return updatedPlans;
    };

    /* =========================================================
       LOAD OTT ASSIGNMENTS FOR ADD-ON PLANS
       
       UI ONLY
       
       We are NOT changing:
       get_ott_addon_plans.php
    ========================================================= */

    const loadAddonOttAssignments = async (
        addonPlans,
        availableOttPlatforms = []
    ) => {

        if (
            !Array.isArray(addonPlans) ||
            addonPlans.length === 0
        ) {

            return {};

        }

        /*
         * The admin side now saves the selected OTT platform IDs
         * against the add-on plan. The add-on-plan API can return
         * those IDs as:
         *
         *   ott_platform_ids: [1, 3, 5]
         *
         * The user page must convert those IDs into the complete OTT
         * objects (name/logo/active) so the existing UI can display
         * them.
         *
         * We keep the old assignment API as a fallback so existing
         * functionality is not broken.
         */
        const normalizeOttIds = (value) => {

            if (Array.isArray(value)) {

                return [
                    ...new Set(
                        value
                            .map((id) => {

                                if (
                                    typeof id ===
                                    "object" &&
                                    id !== null
                                ) {

                                    return Number(
                                        id.id ||
                                        id.ott_platform_id
                                    );

                                }

                                return Number(id);

                            })
                            .filter(
                                (id) =>
                                    Number.isInteger(id) &&
                                    id > 0
                            )
                    )
                ];

            }

            if (
                typeof value ===
                "string" &&
                value.trim()
            ) {

                try {

                    const parsed =
                        JSON.parse(value);

                    if (
                        Array.isArray(
                            parsed
                        )
                    ) {

                        return normalizeOttIds(
                            parsed
                        );

                    }

                } catch (error) {

                    return [
                        ...new Set(
                            value
                                .split(",")
                                .map((id) =>
                                    Number(
                                        String(
                                            id
                                        ).trim()
                                    )
                                )
                                .filter(
                                    (id) =>
                                        Number.isInteger(
                                            id
                                        ) &&
                                        id > 0
                                )
                        )
                    ];

                }

            }

            return [];

        };

        const normalizeOtt = (ott) => {

            if (!ott) {
                return null;
            }

            return {
                ...ott,

                id: Number(
                    ott.id ||
                    ott.ott_platform_id
                ),

                ott_name:
                    ott.ott_name ||
                    ott.name ||
                    "",

                logo:
                    ott.logo ||
                    "",

                active:
                    ott.active ===
                        undefined
                        ? 1
                        : Number(
                            ott.active
                        )
            };

        };

        const getOttsFromIds = (
            addon,
            ids
        ) => {

            if (
                !Array.isArray(
                    availableOttPlatforms
                ) ||
                availableOttPlatforms.length ===
                0 ||
                ids.length === 0
            ) {

                return [];

            }

            const idSet =
                new Set(ids);

            return availableOttPlatforms
                .map(
                    (ott) =>
                        normalizeOtt(ott)
                )
                .filter(
                    (ott) =>
                        ott &&
                        idSet.has(
                            Number(
                                ott.id
                            )
                        ) &&
                        Number(
                            ott.active
                        ) !== 0
                );

        };

        const results =
            await Promise.all(

                addonPlans.map(
                    async (addon) => {

                        try {

                            /*
                             * FIRST:
                             * Use ott_platform_ids returned by
                             * get_ott_addon_plans.php.
                             */
                            const savedOttIds =
                                normalizeOttIds(
                                    addon?.ott_platform_ids ??
                                    addon?.ott_platforms ??
                                    addon?.otts
                                );

                            if (
                                savedOttIds.length >
                                0
                            ) {

                                const assignedFromPlan =
                                    getOttsFromIds(
                                        addon,
                                        savedOttIds
                                    );

                                /*
                                 * If the OTT platform list is already
                                 * available, no extra assignment API
                                 * call is required.
                                 */
                                if (
                                    assignedFromPlan.length >
                                    0
                                ) {

                                    console.log(
                                        `Addon OTTs loaded from ott_platform_ids for ${addon.id}:`,
                                        assignedFromPlan
                                    );

                                    return {
                                        addonId:
                                            addon.id,

                                        otts:
                                            assignedFromPlan
                                    };

                                }

                            }

                            /*
                             * SECOND:
                             * If the new ID-based response cannot be
                             * resolved, keep the existing API as a
                             * backward-compatible fallback.
                             */
                            const res =
                                await axios.get(
                                    `https://ritubroadband.in/api/get_addon_ott_assignments.php?ott_addon_plan_id=${addon.id}`
                                );

                            console.log(
                                `Addon OTT Response for ${addon.id}:`,
                                res.data
                            );

                            if (
                                res.data.status !==
                                "success"
                            ) {

                                return {
                                    addonId:
                                        addon.id,

                                    otts: []
                                };

                            }

                            const assignedOtts =
                                Array.isArray(
                                    res.data.data
                                )
                                    ? res.data.data
                                        .filter(
                                            (ott) =>
                                                Number(
                                                    ott.assigned
                                                ) === 1
                                        )
                                        .filter(
                                            (ott) =>
                                                Number(
                                                    ott.active
                                                ) !== 0
                                        )
                                        .map(
                                            (
                                                ott
                                            ) =>
                                                normalizeOtt(
                                                    ott
                                                )
                                        )
                                        .filter(
                                            Boolean
                                        )
                                    : [];

                            /*
                             * THIRD:
                             * Some versions of the assignment API may
                             * return only OTT IDs. If so, resolve them
                             * against the master OTT platform list.
                             */
                            if (
                                assignedOtts.length >
                                0
                            ) {

                                return {
                                    addonId:
                                        addon.id,

                                    otts:
                                        assignedOtts
                                };

                            }

                            if (
                                savedOttIds.length >
                                0
                            ) {

                                return {
                                    addonId:
                                        addon.id,

                                    otts:
                                        getOttsFromIds(
                                            addon,
                                            savedOttIds
                                        )
                                };

                            }

                            return {
                                addonId:
                                    addon.id,

                                otts: []
                            };

                        } catch (error) {

                            console.error(
                                `Addon OTT assignment error for addon ${addon.id}:`,
                                error
                            );

                            /*
                             * Even when the fallback API fails, try
                             * displaying the saved IDs from the
                             * add-on-plan response.
                             */
                            const savedOttIds =
                                normalizeOttIds(
                                    addon?.ott_platform_ids ??
                                    addon?.ott_platforms ??
                                    addon?.otts
                                );

                            return {
                                addonId:
                                    addon.id,

                                otts:
                                    getOttsFromIds(
                                        addon,
                                        savedOttIds
                                    )
                            };

                        }

                    }
                )

            );

        const addonOttMap = {};

        results.forEach(
            ({
                addonId,
                otts
            }) => {

                addonOttMap[
                    Number(addonId)
                ] = Array.isArray(
                    otts
                )
                        ? otts
                        : [];

            }
        );

        return addonOttMap;
    };

    /* =========================================================
       LOAD ALL BROADBAND / OTT DATA
    ========================================================= */

    useEffect(() => {

        const fetchPlans = async () => {

            try {

                setPlansLoading(true);
                setOttLoading(true);
                setAddonLoading(true);

                const [
                    broadbandResponse,
                    ottResponse,
                    addonResponse
                ] = await Promise.all([

                    axios.get(
                        "https://ritubroadband.in/api/get_broadband_plans.php"
                    ),

                    axios.get(
                        "https://ritubroadband.in/api/get_ott_platforms.php"
                    ),

                    axios.get(
                        "https://ritubroadband.in/api/get_ott_addon_plans.php"
                    )

                ]);

                /* =================================================
                   BROADBAND PLANS
                ================================================= */

                let broadbandPlans = [];

                if (
                    broadbandResponse.data.status ===
                    "success"
                ) {

                    broadbandPlans =
                        Array.isArray(
                            broadbandResponse.data.data
                        )
                            ? broadbandResponse.data.data
                            : [];

                }

                /* =================================================
                   OTT PLATFORMS
                ================================================= */

                if (
                    ottResponse.data.status ===
                    "success"
                ) {

                    setOttPlatforms(
                        Array.isArray(
                            ottResponse.data.data
                        )
                            ? ottResponse.data.data
                            : []
                    );

                } else {

                    setOttPlatforms([]);

                }

                /* =================================================
                   OTT ADD-ON PLANS
                ================================================= */

                let addonPlans = [];

                if (
                    addonResponse.data.status ===
                    "success"
                ) {

                    addonPlans =
                        Array.isArray(
                            addonResponse.data.data
                        )
                            ? addonResponse.data.data
                            : [];

                    setOttAddonPlans(
                        addonPlans
                    );

                    /*
                     * Load OTTs assigned to
                     * each add-on plan.
                     */
                    const addonOttMap =
                        await loadAddonOttAssignments(
                            addonPlans,
                            Array.isArray(
                                ottResponse.data.data
                            )
                                ? ottResponse.data.data
                                : []
                        );

                    setAddonOtts(
                        addonOttMap
                    );

                } else {

                    setOttAddonPlans([]);
                    setAddonOtts({});

                }

                /* =================================================
                   LOAD BROADBAND DURATION OTTs
                ================================================= */

                const plansWithOtt =
                    await loadOttAssignments(
                        broadbandPlans
                    );

                console.log(
                    "Broadband Plans With OTT:",
                    plansWithOtt
                );

                setPlans(
                    plansWithOtt
                );

            } catch (error) {

                console.error(
                    "Plans loading error:",
                    error
                );

                setPlans([]);
                setOttPlatforms([]);
                setOttAddonPlans([]);
                setAddonOtts({});

            } finally {

                setPlansLoading(false);
                setOttLoading(false);
                setAddonLoading(false);

            }

        };

        fetchPlans();

    }, []);

    /* =========================================================
       DYNAMIC SPEED TABS
    ========================================================= */

    const speedTabs = [
        ...new Map(

            plans.map(
                (plan) => [

                    Number(
                        plan.speed
                    ),

                    {
                        speed:
                            Number(
                                plan.speed
                            ),

                        label:
                            `${plan.speed} Mbps`
                    }

                ]
            )

        ).values()

    ].sort(
        (a, b) =>
            a.speed - b.speed
    );

    /* =========================================================
       DEFAULT SELECTED SPEED
    ========================================================= */

    useEffect(() => {

        if (
            plans.length > 0 &&
            selectedSpeed === null
        ) {

            const firstSpeed =
                Number(
                    plans[0].speed
                );

            setSelectedSpeed(
                firstSpeed
            );

        }

    }, [
        plans,
        selectedSpeed
    ]);

    /* =========================================================
       FILTER BY SPEED
    ========================================================= */

    const filteredPlans =
        selectedSpeed === null

            ? plans

            : plans.filter(
                (plan) =>
                    Number(
                        plan.speed
                    ) ===
                    Number(
                        selectedSpeed
                    )
            );

    /* =========================================================
       UPCOMING PLAN HELPER
    ========================================================= */

    const checkIsUpcomingPlan = (
        plan,
        duration
    ) => {

        if (!hasUpcomingBroadbandPlan()) {
            return false;
        }

        const scheduledPlanType = String(
            upcomingBroadbandPlan.plan_type || ""
        )
            .trim()
            .toUpperCase();

        const scheduledPlanStatus = String(
            upcomingBroadbandPlan.plan_status ||
            upcomingBroadbandPlan.status ||
            ""
        )
            .trim()
            .toUpperCase();

        const scheduledPaymentStatus = String(
            upcomingBroadbandPlan.payment_status || ""
        )
            .trim()
            .toUpperCase();

        if (
            scheduledPlanType !== "BROADBAND" ||
            scheduledPlanStatus !== "SCHEDULED" ||
            scheduledPaymentStatus !== "SUCCESS"
        ) {
            return false;
        }

        const upcomingPlanName =
            String(
                upcomingBroadbandPlan.plan_name || ""
            )
                .trim()
                .toLowerCase();

        const displayedPlanName =
            String(
                plan.plan_name || ""
            )
                .trim()
                .toLowerCase();

        if (
            upcomingPlanName !==
            displayedPlanName
        ) {
            return false;
        }

        const upcomingMonths =
            Number(
                upcomingBroadbandPlan.duration_months ||
                upcomingBroadbandPlan.months ||
                0
            );

        const displayedMonths =
            Number(
                duration.months ||
                duration.duration_months ||
                0
            );

        if (
            upcomingMonths > 0 &&
            displayedMonths > 0
        ) {
            return (
                upcomingMonths ===
                displayedMonths
            );
        }

        /*
         * Final fallback:
         * compare the plan amount.
         */
        return (
            Number(
                upcomingBroadbandPlan.amount || 0
            ) ===
            Number(
                duration.price || 0
            )
        );
    };

    /* =========================================================
       CURRENT PLAN HELPER
    ========================================================= */

    const checkIsCurrentPlan = (
        plan,
        duration
    ) => {

        if (!currentBroadbandPlan) {

            return false;

        }

        const currentPlanType =
            String(
                currentBroadbandPlan.plan_type ||
                ""
            )
                .trim()
                .toUpperCase();

        if (
            currentPlanType !==
            "BROADBAND"
        ) {

            return false;

        }

        const currentStatus =
            String(
                currentBroadbandPlan.plan_status ||
                ""
            )
                .trim()
                .toUpperCase();

        if (
            currentStatus !==
            "ACTIVE"
        ) {

            return false;

        }

        const currentPlanName =
            String(
                currentBroadbandPlan.plan_name ||
                ""
            )
                .trim()
                .toLowerCase();

        const displayedPlanName =
            String(
                plan.plan_name ||
                ""
            )
                .trim()
                .toLowerCase();

        if (
            currentPlanName !==
            displayedPlanName
        ) {

            return false;

        }

        const currentMonths =
            Number(
                currentBroadbandPlan.duration_months ||
                currentBroadbandPlan.months ||
                0
            );

        const displayedMonths =
            Number(
                duration.months ||
                duration.duration_months ||
                0
            );

        if (
            currentMonths > 0 &&
            displayedMonths > 0
        ) {

            return (
                currentMonths ===
                displayedMonths
            );

        }

        return (
            Number(
                currentBroadbandPlan.amount || 0
            ) ===
            Number(
                duration.price || 0
            )
        );

    };

    /* =========================================================

    /* =========================================================
       PENDING BROADBAND PAYMENT
       =========================================================

       New payment flow:
         payment_status = SUCCESS
         plan_status    = PENDING

       This means payment is complete, but the administrator
       still has to activate the broadband plan.

       The purchased plan therefore remains visible in the
       normal broadband plan cards with a PENDING status.
    ========================================================= */

    const isPendingBroadbandPayment = (payment) => {

        if (!payment) {
            return false;
        }

        const paymentStatus = String(
            payment.payment_status || ""
        )
            .trim()
            .toUpperCase();

        const planStatus = String(
            payment.plan_status || ""
        )
            .trim()
            .toUpperCase();

        const planType = String(
            payment.plan_type || "BROADBAND"
        )
            .trim()
            .toUpperCase();

        return (
            paymentStatus === "SUCCESS" &&
            planStatus === "PENDING" &&
            planType === "BROADBAND"
        );
    };

    /*
     * Find the successful/pending broadband payment that belongs
     * to the plan card currently being rendered.
     */
    const getPendingBroadbandPayment = (
        plan,
        duration
    ) => {

        if (
            !Array.isArray(broadbandPaymentRecords) ||
            broadbandPaymentRecords.length === 0
        ) {
            return null;
        }

        const displayedPlanName = String(
            plan?.plan_name || ""
        )
            .trim()
            .toLowerCase();

        const displayedMonths = Number(
            duration?.months ??
            duration?.duration_months ??
            0
        );

        const displayedPrice = Number(
            duration?.price ?? 0
        );

        const matches = broadbandPaymentRecords.filter(
            (payment) => {

                if (
                    !isPendingBroadbandPayment(
                        payment
                    )
                ) {
                    return false;
                }

                const paymentPlanName = String(
                    payment.plan_name || ""
                )
                    .trim()
                    .toLowerCase();

                if (
                    paymentPlanName !==
                    displayedPlanName
                ) {
                    return false;
                }

                const paymentMonths = Number(
                    payment.duration_months ??
                    payment.months ??
                    0
                );

                const paymentAmount = Number(
                    payment.amount ?? 0
                );

                /*
                 * If both records have duration data, it must match.
                 */
                if (
                    displayedMonths > 0 &&
                    paymentMonths > 0 &&
                    displayedMonths !== paymentMonths
                ) {
                    return false;
                }

                /*
                 * If both records have amount data, it must match.
                 */
                if (
                    displayedPrice > 0 &&
                    paymentAmount > 0 &&
                    displayedPrice !== paymentAmount
                ) {
                    return false;
                }

                return true;
            }
        );

        if (matches.length === 0) {
            return null;
        }

        /*
         * If there is more than one matching pending payment,
         * use the latest transaction.
         */
        return [...matches].sort(
            (a, b) => {

                const aTime =
                    parseIndiaDateTime(
                        a.transaction_date
                    )?.getTime() || 0;

                const bTime =
                    parseIndiaDateTime(
                        b.transaction_date
                    )?.getTime() || 0;

                return bTime - aTime;
            }
        )[0];
    };


    /*
     * Latest successful broadband payment that is still waiting
     * for administrator activation.
     */
    const pendingBroadbandPayment =
        Array.isArray(broadbandPaymentRecords)
            ? [...broadbandPaymentRecords]
                .filter(
                    (payment) =>
                        isPendingBroadbandPayment(
                            payment
                        )
                )
                .sort(
                    (a, b) => {

                        const aTime =
                            parseIndiaDateTime(
                                a.transaction_date
                            )?.getTime() || 0;

                        const bTime =
                            parseIndiaDateTime(
                                b.transaction_date
                            )?.getTime() || 0;

                        return bTime - aTime;
                    }
                )[0] || null
            : null;

    /*
     * Latest successful add-on payments that are still waiting
     * for administrator activation.
     */
    const pendingAddonPayments =
        Array.isArray(addonPaymentRecords)
            ? [...addonPaymentRecords]
                .filter(
                    (payment) =>
                        isPendingAddonPayment(
                            payment
                        )
                )
                .sort(
                    (a, b) => {

                        const aTime =
                            parseIndiaDateTime(
                                a.transaction_date
                            )?.getTime() ||
                            Number(a.id || 0);

                        const bTime =
                            parseIndiaDateTime(
                                b.transaction_date
                            )?.getTime() ||
                            Number(b.id || 0);

                        return bTime - aTime;
                    }
                )
            : [];


    //    RENDER
    // ========================================================= */

    return (

        <div className="dashboard-wrapper">

            <ToastContainer
                position="top-right"
                autoClose={3500}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="container py-5">

                {/* =====================================================
                    HEADER
                ====================================================== */}

                <div className="dashboard-header-card">

                    <div className="header-card-inner">

                        <div className="header-user-profile">

                            <div className="profile-avatar-glow">

                                <div className="profile-avatar-circle">

                                    {
                                        user.fullname
                                            ? user.fullname
                                                .charAt(0)
                                                .toUpperCase()
                                            : "U"
                                    }

                                </div>

                            </div>

                            <div className="profile-welcome-info">

                                <div className="profile-title-row">
                                    <div>
                                        <span className="welcome-tagline">
                                            Welcome back,
                                        </span>

                                        <h2 className="welcome-name">
                                            {
                                                user.fullname ||
                                                "User"
                                            }
                                        </h2>
                                    </div>

                                    <button 
                                        onClick={handleOpenEditModal}
                                        className="profile-edit-btn"
                                        title="Edit Profile"
                                    >
                                        <FaEdit size={13} />
                                        <span>Edit</span>
                                    </button>
                                </div>

                                <div className="profile-contact-meta">

                                    <span className="contact-meta-item">
                                        <Phone size={13} />
                                        <span>
                                            {
                                                user.mobile ||
                                                "N/A"
                                            }
                                        </span>
                                    </span>

                                    <span className="contact-meta-item contact-meta-email">
                                        <Mail size={13} />
                                        <span className="email-truncate">
                                            {
                                                user.email ||
                                                "N/A"
                                            }
                                        </span>
                                    </span>

                                </div>

                            </div>


                        </div>

                        <div className="header-user-status">

                            <div className="status-badge-container">

                                <span
                                    className={`status-pill ${user.status ===
                                        "active"
                                        ? "status-active"
                                        : "status-inactive"
                                        }`}
                                >

                                    <span className="pulse-dot"></span>

                                    {
                                        user.status ===
                                            "active"
                                            ? "Active Connection"
                                            : "Inactive Account"
                                    }

                                </span>

                            </div>

                            <span className="connection-line-indicator">

                                <Activity
                                    size={14}
                                    className="activity-pulse-icon"
                                />

                                <span>

                                    {
                                        user.status ===
                                            "active"
                                            ? "Connected to Fiber"
                                            : "Disconnected"
                                    }

                                </span>

                            </span>

                        </div>

                    </div>

                </div>

                {/* =====================================================
                    CURRENT ACTIVE BROADBAND SUBSCRIPTION
                ====================================================== */}

                {!loading &&
                    currentBroadbandPlan && (

                        <div className="active-subscription-widget">
                            {/* Desktop Version */}
                            <div className="desktop-sub-widget">
                                <div className="subscription-widget-header">
                                    <div className="widget-header-title">
                                        <div className="wifi-icon-ring">
                                            <Wifi
                                                size={20}
                                                className="floating-wifi-icon"
                                            />
                                        </div>
                                        <div className="widget-title-text">
                                            <h4>
                                                Active Broadband Subscription
                                            </h4>
                                            <p>
                                                Your current internet
                                                connection plan and
                                                validity details
                                            </p>
                                        </div>
                                    </div>
                                    <span className="active-status-badge">
                                        <ShieldCheck size={14} />
                                        <span>
                                            {
                                                currentBroadbandPlan.plan_status
                                            }
                                        </span>
                                    </span>
                                </div>
                                <div className="subscription-widget-body">
                                    <div className="subscription-details-grid">
                                        <div className="sub-detail-card">
                                            <span className="sub-detail-label">
                                                Active Plan Speed
                                            </span>
                                            <h3 className="sub-detail-value-speed">
                                                {
                                                    currentBroadbandPlan.plan_name
                                                }
                                            </h3>
                                        </div>
                                        <div className="sub-detail-card">
                                            <span className="sub-detail-label">
                                                Amount Paid
                                            </span>
                                            <h3 className="sub-detail-value-price">
                                                ₹
                                                {
                                                    currentBroadbandPlan.amount
                                                }
                                            </h3>
                                        </div>
                                        <div className="sub-detail-card">
                                            <span className="sub-detail-label">
                                                Billing Cycle
                                            </span>
                                            <span className="sub-detail-text">
                                                {
                                                    currentBroadbandPlan.duration_label ||
                                                    (
                                                        currentBroadbandPlan.duration_months
                                                            ? `${currentBroadbandPlan.duration_months} Month${Number(
                                                                currentBroadbandPlan.duration_months
                                                            ) > 1
                                                                ? "s"
                                                                : ""
                                                            }`
                                                            : "Monthly (30 Days)"
                                                    )
                                                }
                                            </span>
                                        </div>
                                        <div className="sub-detail-card">
                                            <span className="sub-detail-label">
                                                Payment Method
                                            </span>
                                            <span className="sub-detail-text-badge">
                                                {
                                                    currentBroadbandPlan.payment_method ||
                                                    "ONLINE"
                                                }
                                            </span>
                                        </div>
                                        <div className="sub-detail-card">
                                            <span className="sub-detail-label">
                                                Activated On
                                            </span>
                                            <div className="sub-date-info">
                                                <Calendar size={13} />
                                                <span>
                                                    {
                                                        formatPlanDate(
                                                            currentBroadbandPlan.plan_start_date
                                                        )
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                        <div className="sub-detail-card highlight-expiry">
                                            <span className="sub-detail-label">
                                                Expires On
                                            </span>
                                            <div className="sub-date-info text-danger-custom">
                                                <Clock size={13} />
                                                <span>
                                                    {
                                                        formatPlanDate(
                                                            currentBroadbandPlan.plan_expiry_date
                                                        )
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile View - Single Compact Telecom Card (Image 2 style) */}
                            <div className="mobile-telecom-card">
                                <div className="telecom-card-header">
                                    <div className="telecom-header-info">
                                        <span className="telecom-pack-badge">PACK</span>
                                        <h3 className="telecom-pack-title">
                                            ₹{currentBroadbandPlan.amount} {currentBroadbandPlan.plan_name || "Unlimited"}
                                        </h3>
                                    </div>
                                    <span className="telecom-active-pill">
                                        <span className="telecom-active-dot"></span>
                                        {currentBroadbandPlan.plan_status || "Active"}
                                    </span>
                                </div>

                                <div className="telecom-stats-row">
                                    <div className="telecom-stat-col">
                                        <span className="telecom-stat-label">Speed</span>
                                        <span className="telecom-stat-val">{currentBroadbandPlan.plan_name || "High Speed"}</span>
                                    </div>
                                    <div className="telecom-stat-col">
                                        <span className="telecom-stat-label">Billing</span>
                                        <span className="telecom-stat-val">
                                            {currentBroadbandPlan.duration_label ||
                                                (currentBroadbandPlan.duration_months
                                                    ? `${currentBroadbandPlan.duration_months} Month${Number(currentBroadbandPlan.duration_months) > 1 ? "s" : ""}`
                                                    : "1 Month")}
                                        </span>
                                    </div>
                                    <div className="telecom-stat-col">
                                        <span className="telecom-stat-label">Payment</span>
                                        <span className="telecom-stat-val">
                                            {currentBroadbandPlan.payment_method ? currentBroadbandPlan.payment_method.toUpperCase() : "ONLINE"}
                                        </span>
                                    </div>
                                </div>

                                <div className="telecom-validity-row">
                                    <div className="telecom-validity-col">
                                        <span className="telecom-validity-label">Valid Till</span>
                                        <span className="telecom-validity-val">
                                            {formatPlanDate(currentBroadbandPlan.plan_expiry_date)}
                                        </span>
                                    </div>
                                    <div className="telecom-validity-col text-end">
                                        <span className="telecom-validity-label">Activated On</span>
                                        <span className="telecom-validity-sub">
                                            {formatPlanDate(currentBroadbandPlan.plan_start_date)}
                                        </span>
                                    </div>
                                </div>

                                {showMobileSubDetails && (
                                    <div className="telecom-expanded-details">
                                        <div className="telecom-detail-item">
                                            <span>Subscription Status:</span>
                                            <strong className="text-success">{currentBroadbandPlan.plan_status}</strong>
                                        </div>
                                        <div className="telecom-detail-item">
                                            <span>Plan Speed:</span>
                                            <strong>{currentBroadbandPlan.plan_name}</strong>
                                        </div>
                                        <div className="telecom-detail-item">
                                            <span>Amount Paid:</span>
                                            <strong>₹{currentBroadbandPlan.amount}</strong>
                                        </div>
                                        <div className="telecom-detail-item">
                                            <span>Payment Method:</span>
                                            <strong>{currentBroadbandPlan.payment_method || "ONLINE"}</strong>
                                        </div>
                                        <div className="telecom-detail-item">
                                            <span>Start Date:</span>
                                            <span>{formatPlanDate(currentBroadbandPlan.plan_start_date)}</span>
                                        </div>
                                        <div className="telecom-detail-item">
                                            <span>Expiry Date:</span>
                                            <span className="text-danger fw-semibold">{formatPlanDate(currentBroadbandPlan.plan_expiry_date)}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="telecom-actions-row">
                                    <button 
                                        type="button" 
                                        className="telecom-btn telecom-btn-details"
                                        onClick={() => setShowMobileSubDetails(prev => !prev)}
                                    >
                                        {showMobileSubDetails ? "Hide Details" : "View Details"}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="telecom-btn telecom-btn-recharge"
                                        onClick={() => {
                                            const plansElem = document.getElementById("fiber-plans-section");
                                            if (plansElem) {
                                                plansElem.scrollIntoView({ behavior: "smooth", block: "start" });
                                            }
                                        }}
                                    >
                                        Recharge
                                    </button>
                                </div>
                            </div>
                        </div>


                    )}

                {/* =====================================================
                    PENDING BROADBAND ACTIVATION
                    =====================================================

                    Payment is SUCCESS but plan_status is PENDING.

                    Show the purchased plan even though it is not active
                    yet. The admin will activate it manually.
                ====================================================== */}

                {!loading &&
                    !broadbandPaymentStatusLoading &&
                    !currentBroadbandPlan &&
                    pendingBroadbandPayment && (

                        <div className="active-subscription-widget pending-activation-widget">

                            <div className="subscription-widget-header">

                                <div className="widget-header-title">

                                    <div className="wifi-icon-ring pending-activation-icon-ring">

                                        <Clock
                                            size={20}
                                            className="floating-wifi-icon"
                                        />

                                    </div>

                                    <div className="widget-title-text">

                                        <h4>
                                            Broadband Plan Activation Pending
                                        </h4>

                                        <p>
                                            Your payment was completed successfully,
                                            but your broadband plan is waiting for
                                            administrator activation.
                                        </p>

                                    </div>

                                </div>

                                <span className="active-status-badge pending-activation-badge">

                                    <Clock size={14} />

                                    <span>
                                        PENDING
                                    </span>

                                </span>

                            </div>

                            <div className="subscription-widget-body">

                                <div className="pending-activation-notice">

                                    <div className="pending-activation-notice-icon">

                                        <CheckCircle size={18} />

                                    </div>

                                    <div>

                                        <strong>
                                            Payment completed successfully.
                                        </strong>

                                        <p>
                                            Your plan activation is pending.
                                            It takes some time to activate.
                                        </p>

                                    </div>

                                </div>

                                <div className="subscription-details-grid">

                                    <div className="sub-detail-card">

                                        <span className="sub-detail-label">
                                            Purchased Plan
                                        </span>

                                        <h3 className="sub-detail-value-speed">
                                            {
                                                pendingBroadbandPayment.plan_name ||
                                                "Broadband Plan"
                                            }
                                        </h3>

                                    </div>

                                    <div className="sub-detail-card">

                                        <span className="sub-detail-label">
                                            Amount Paid
                                        </span>

                                        <h3 className="sub-detail-value-price">

                                            ₹
                                            {Number(
                                                pendingBroadbandPayment.amount ||
                                                0
                                            ).toFixed(2)}

                                        </h3>

                                    </div>

                                    <div className="sub-detail-card">

                                        <span className="sub-detail-label">
                                            Payment Status
                                        </span>

                                        <span className="sub-detail-text-badge pending-payment-status-badge">

                                            SUCCESS

                                        </span>

                                    </div>

                                    <div className="sub-detail-card">

                                        <span className="sub-detail-label">
                                            Plan Status
                                        </span>

                                        <span className="sub-detail-text-badge pending-plan-status-badge">

                                            PENDING

                                        </span>

                                    </div>

                                    <div className="sub-detail-card">

                                        <span className="sub-detail-label">
                                            Payment Date
                                        </span>

                                        <div className="sub-date-info">

                                            <Calendar size={13} />

                                            <span>
                                                {
                                                    formatIndiaDateTime(
                                                        pendingBroadbandPayment.transaction_date
                                                    )
                                                }
                                            </span>

                                        </div>

                                    </div>

                                    <div className="sub-detail-card">

                                        <span className="sub-detail-label">
                                            Activation
                                        </span>

                                        <div className="sub-date-info pending-activation-date">

                                            <Clock size={13} />

                                            <span>
                                                Waiting for admin activation
                                            </span>

                                        </div>

                                    </div>

                                </div>

                            </div>

                        </div>

                    )}

                {/* =====================================================
                    UPCOMING / CONTINUATION BROADBAND PLAN
                ====================================================== */}

                {!loading &&
                    !upcomingPlanLoading &&
                    hasUpcomingBroadbandPlan() && (

                        <div className="active-subscription-widget upcoming-subscription-widget">

                            {/* Desktop View */}
                            <div className="desktop-sub-widget">

                                <div className="subscription-widget-header">

                                    <div className="widget-header-title">

                                        <div className="wifi-icon-ring upcoming-plan-icon-ring">

                                            <Clock
                                                size={20}
                                                className="floating-wifi-icon"
                                            />

                                        </div>

                                        <div className="widget-title-text">

                                            <h4>
                                                Upcoming Broadband Plan
                                            </h4>

                                            <p>
                                                Your purchased broadband plan is scheduled
                                                and will become active according to its
                                                scheduled start date.
                                            </p>

                                        </div>

                                    </div>

                                    <span className="active-status-badge upcoming-status-badge">

                                        <Clock size={14} />

                                        <span>
                                            SCHEDULED
                                        </span>

                                    </span>

                                </div>

                                <div className="subscription-widget-body">

                                    <div className="continuation-notice">

                                        <div className="continuation-notice-icon">

                                            <CheckCircle size={18} />

                                        </div>

                                        <div>

                                            <strong>
                                                Broadband Plan Scheduled Successfully
                                            </strong>

                                            <p>
                                                Your scheduled plan will start on{" "}
                                                <strong>
                                                    {formatPlanDate(
                                                        upcomingBroadbandPlan?.plan_start_date
                                                    )}
                                                </strong>
                                                .
                                            </p>

                                        </div>

                                    </div>

                                    <div className="subscription-details-grid">

                                        <div className="sub-detail-card">

                                            <span className="sub-detail-label">
                                                Scheduled Plan
                                            </span>

                                            <h3 className="sub-detail-value-speed">
                                                {
                                                    upcomingBroadbandPlan.plan_name
                                                }
                                            </h3>

                                        </div>

                                        <div className="sub-detail-card">

                                            <span className="sub-detail-label">
                                                Amount Paid
                                            </span>

                                            <h3 className="sub-detail-value-price">
                                                ₹
                                                {Number(
                                                    upcomingBroadbandPlan.amount || 0
                                                ).toFixed(2)}
                                            </h3>

                                        </div>

                                        <div className="sub-detail-card">

                                            <span className="sub-detail-label">
                                                Billing Cycle
                                            </span>

                                            <span className="sub-detail-text">

                                                {
                                                    upcomingBroadbandPlan.duration_label ||
                                                    (
                                                        upcomingBroadbandPlan.duration_months
                                                            ? `${upcomingBroadbandPlan.duration_months} Month${Number(
                                                                upcomingBroadbandPlan.duration_months
                                                            ) > 1
                                                                ? "s"
                                                                : ""
                                                            }`
                                                            : "Monthly"
                                                    )
                                                }

                                            </span>

                                        </div>

                                        <div className="sub-detail-card">

                                            <span className="sub-detail-label">
                                                Payment Method
                                            </span>

                                            <span className="sub-detail-text-badge">

                                                {
                                                    upcomingBroadbandPlan.payment_method ||
                                                    "ONLINE"
                                                }

                                            </span>

                                        </div>

                                        <div className="sub-detail-card">

                                            <span className="sub-detail-label">
                                                Starts On
                                            </span>

                                            <div className="sub-date-info">

                                                <Calendar size={13} />

                                                <span>
                                                    {
                                                        formatPlanDate(
                                                            upcomingBroadbandPlan.plan_start_date
                                                        )
                                                    }
                                                </span>

                                            </div>

                                        </div>

                                        <div className="sub-detail-card">

                                            <span className="sub-detail-label">
                                                Expires On
                                            </span>

                                            <div className="sub-date-info">

                                                <Clock size={13} />

                                                <span>
                                                    {
                                                        formatPlanDate(
                                                            upcomingBroadbandPlan.plan_expiry_date
                                                        )
                                                    }
                                                </span>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            </div>

                            {/* Mobile View - Scheduled Telecom Card */}
                            <div className="mobile-telecom-card scheduled-telecom-card">

                                {/* Success notice banner */}
                                <div className="scheduled-notice-banner">
                                    <CheckCircle size={15} />
                                    <span>
                                        <strong>Broadband Plan Scheduled Successfully</strong>
                                        &nbsp;· Your scheduled plan will start on{" "}
                                        <strong>{formatPlanDate(upcomingBroadbandPlan?.plan_start_date)}</strong>
                                    </span>
                                </div>

                                <div className="telecom-card-header">
                                    <div className="telecom-header-info">
                                        <span className="telecom-pack-badge">SCHEDULED PLAN</span>
                                        <h3 className="telecom-pack-title">
                                            {upcomingBroadbandPlan.plan_name || "Broadband Plan"}
                                        </h3>
                                    </div>
                                    <span className="telecom-active-pill scheduled-pill">
                                        <Clock size={12} />
                                        SCHEDULED
                                    </span>
                                </div>

                                <div className="telecom-stats-row">
                                    <div className="telecom-stat-col">
                                        <span className="telecom-stat-label">Amount Paid</span>
                                        <span className="telecom-stat-val">
                                            ₹{Number(upcomingBroadbandPlan.amount || 0).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="telecom-stat-col">
                                        <span className="telecom-stat-label">Billing Cycle</span>
                                        <span className="telecom-stat-val">
                                            {upcomingBroadbandPlan.duration_label ||
                                                (upcomingBroadbandPlan.duration_months
                                                    ? `${upcomingBroadbandPlan.duration_months} Month${Number(upcomingBroadbandPlan.duration_months) > 1 ? "s" : ""}`
                                                    : "1 Month")}
                                        </span>
                                    </div>
                                    <div className="telecom-stat-col">
                                        <span className="telecom-stat-label">Payment</span>
                                        <span className="telecom-stat-val">
                                            {upcomingBroadbandPlan.payment_method
                                                ? upcomingBroadbandPlan.payment_method.toUpperCase()
                                                : "ONLINE"}
                                        </span>
                                    </div>
                                </div>

                                <div className="telecom-validity-row">
                                    <div className="telecom-validity-col">
                                        <span className="telecom-validity-label">Starts On</span>
                                        <span className="telecom-validity-val">
                                            {formatPlanDate(upcomingBroadbandPlan.plan_start_date)}
                                        </span>
                                    </div>
                                    <div className="telecom-validity-col text-end">
                                        <span className="telecom-validity-label">Expires On</span>
                                        <span className="telecom-validity-sub">
                                            {formatPlanDate(upcomingBroadbandPlan.plan_expiry_date)}
                                        </span>
                                    </div>
                                </div>

                            </div>

                        </div>
                    )}

                {/* =====================================================
                    ACTIVE OTT ADD-ONS
                    Kept completely separate from the broadband card.
                ====================================================== */}

                {!activeAddonsLoading && activeAddons.length > 0 && (
                    <div className="active-subscription-widget active-addon-subscription-widget">

                        <div className="subscription-widget-header">

                            <div className="widget-header-title">

                                <div className="wifi-icon-ring">
                                    <Tv
                                        size={20}
                                        className="floating-wifi-icon"
                                    />
                                </div>

                                <div className="widget-title-text">

                                    <h4>
                                        Active OTT Add-ons
                                    </h4>

                                    <p>
                                        Your active entertainment add-ons
                                        and validity details
                                    </p>

                                </div>

                            </div>

                            <span className="active-status-badge">

                                <CheckCircle size={14} />

                                <span>
                                    {activeAddons.length > 0
                                        ? "ACTIVE"
                                        : "NO ACTIVE ADD-ON"}
                                </span>

                            </span>

                        </div>

                        {activeAddons.length > 0 ? (

                            <div className="subscription-widget-body">

                                <div className="subscription-details-grid">

                                    {activeAddons.map((addon, index) => {

                                        const addonName =
                                            addon.addon_plan_name ||
                                            addon.plan_name ||
                                            addon.duration_label ||
                                            "OTT Add-on";

                                        const addonAmount =
                                            addon.amount ??
                                            addon.price ??
                                            0;

                                        const addonDuration =
                                            addon.duration_label ||
                                            (
                                                addon.duration_months
                                                    ? `${addon.duration_months} Month${Number(
                                                        addon.duration_months
                                                    ) > 1
                                                        ? "s"
                                                        : ""
                                                    }`
                                                    : "Monthly"
                                            );

                                        const addonStart =
                                            formatPlanDate(
                                                addon.start_date ||
                                                addon.plan_start_date ||
                                                "-"
                                            );

                                        const addonExpiry =
                                            formatPlanDate(
                                                addon.expiry_date ||
                                                addon.plan_expiry_date ||
                                                "-"
                                            );

                                        return (
                                            <React.Fragment
                                                key={
                                                    addon.id ||
                                                    `addon-${index}`
                                                }
                                            >

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        OTT Add-on
                                                    </span>

                                                    <h3 className="sub-detail-value-speed">
                                                        {addonName}
                                                    </h3>

                                                </div>

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Amount Paid
                                                    </span>

                                                    <h3 className="sub-detail-value-price">
                                                        ₹
                                                        {Number(
                                                            addonAmount
                                                        ).toFixed(2)}
                                                    </h3>

                                                </div>

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Billing Cycle
                                                    </span>

                                                    <span className="sub-detail-text">
                                                        {addonDuration}
                                                    </span>

                                                </div>

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Payment Method
                                                    </span>

                                                    <span className="sub-detail-text-badge">
                                                        {
                                                            addon.payment_method ||
                                                            "ONLINE"
                                                        }
                                                    </span>

                                                </div>

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Activated On
                                                    </span>

                                                    <div className="sub-date-info">

                                                        <Calendar size={13} />

                                                        <span>
                                                            {addonStart}
                                                        </span>

                                                    </div>

                                                </div>

                                                <div className="sub-detail-card highlight-expiry">

                                                    <span className="sub-detail-label">
                                                        Expires On
                                                    </span>

                                                    <div className="sub-date-info text-danger-custom">

                                                        <Clock size={13} />

                                                        <span>
                                                            {addonExpiry}
                                                        </span>

                                                    </div>

                                                </div>

                                            </React.Fragment>
                                        );
                                    })}

                                </div>

                            </div>

                        ) : (

                            <div className="subscription-widget-body">

                                <div
                                    style={{
                                        padding: "30px",
                                        textAlign: "center"
                                    }}
                                >

                                    <Tv size={30} />

                                    <h5>
                                        No Active OTT Add-ons
                                    </h5>

                                    <p>
                                        You don't have any active OTT
                                        add-ons currently.
                                    </p>

                                </div>

                            </div>

                        )}

                    </div>
                )}

                {/* =====================================================
                    PENDING OTT ADD-ONS ACTIVATION
                    =====================================================

                    Payment is SUCCESS but plan_status is PENDING for an addon.
                    
                    Show the purchased addon even though it is not active
                    yet. The admin will activate it manually.
                ====================================================== */}
                {!loading &&
                    !addonPaymentStatusLoading &&
                    pendingAddonPayments &&
                    pendingAddonPayments.length > 0 && (

                        <div className="active-subscription-widget pending-activation-widget pending-addon-activation-widget mt-4">

                            <div className="subscription-widget-header">

                                <div className="widget-header-title">

                                    <div className="wifi-icon-ring pending-activation-icon-ring">

                                        <Clock
                                            size={20}
                                            className="floating-wifi-icon"
                                        />

                                    </div>

                                    <div className="widget-title-text">

                                        <h4>
                                            OTT Add-on Activation Pending
                                        </h4>

                                        <p>
                                            Your payment was completed successfully,
                                            but your OTT add-on plan is waiting for
                                            administrator activation.
                                        </p>

                                    </div>

                                </div>

                                <span className="active-status-badge pending-activation-badge">

                                    <Clock size={14} />

                                    <span>
                                        PENDING
                                    </span>

                                </span>

                            </div>

                            <div className="subscription-widget-body">

                                <div className="pending-activation-notice">

                                    <div className="pending-activation-notice-icon">

                                        <CheckCircle size={18} />

                                    </div>

                                    <div>

                                        <strong>
                                            Payment completed successfully.
                                        </strong>

                                        <p>
                                            Your add-on activation is pending.
                                            It takes some time to activate.
                                        </p>

                                    </div>

                                </div>

                                <div className="subscription-details-grid">

                                    {pendingAddonPayments.map((addonPayment, idx) => {
                                        const addonName =
                                            addonPayment.plan_name ||
                                            addonPayment.addon_plan_name ||
                                            addonPayment.duration_label ||
                                            "OTT Add-on";

                                        const addonAmount =
                                            addonPayment.amount ??
                                            addonPayment.price ??
                                            0;

                                        return (
                                            <React.Fragment key={addonPayment.id || `pending-addon-${idx}`}>
                                                {idx > 0 && <div className="col-12" style={{ gridColumn: "1 / -1" }}><hr className="addon-divider-hr" /></div>}

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Purchased Add-on
                                                    </span>

                                                    <h3 className="sub-detail-value-speed">
                                                        {addonName}
                                                    </h3>

                                                </div>

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Amount Paid
                                                    </span>

                                                    <h3 className="sub-detail-value-price">
                                                        ₹{Number(addonAmount).toFixed(2)}
                                                    </h3>

                                                </div>

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Payment Status
                                                    </span>

                                                    <span className="sub-detail-text-badge pending-payment-status-badge">
                                                        SUCCESS
                                                    </span>

                                                </div>

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Plan Status
                                                    </span>

                                                    <span className="sub-detail-text-badge pending-plan-status-badge">
                                                        PENDING
                                                    </span>

                                                </div>

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Payment Date
                                                    </span>

                                                    <div className="sub-date-info">

                                                        <Calendar size={13} />

                                                        <span>
                                                            {formatIndiaDateTime(
                                                                addonPayment.transaction_date
                                                            )}
                                                        </span>

                                                    </div>

                                                </div>

                                                <div className="sub-detail-card">

                                                    <span className="sub-detail-label">
                                                        Activation
                                                    </span>

                                                    <div className="sub-date-info pending-activation-date">

                                                        <Clock size={13} />

                                                        <span>
                                                            Waiting for admin activation
                                                        </span>

                                                    </div>

                                                </div>
                                            </React.Fragment>
                                        );
                                    })}

                                </div>

                            </div>

                        </div>

                    )}

                {/* =====================================================
                    BROADBAND PLANS HEADER
                ====================================================== */}

                <div className="plans-section-intro" id="fiber-plans-section">

                    <div className="intro-line"></div>

                    <h2>
                        Explore Premium Fiber Plans
                    </h2>

                    <p>
                        Upgrade or renew your internet experience
                        with symmetrical fiber connectivity
                    </p>

                </div>

                {/* =====================================================
                    SPEED TABS
                ====================================================== */}

                {!plansLoading &&
                    speedTabs.length > 0 && (

                        <div className="speed-tabs-wrapper">

                            <div className="speed-tabs">

                                {speedTabs.map(
                                    (tab) => (

                                        <button
                                            type="button"
                                            key={
                                                tab.speed
                                            }
                                            className={`speed-tab ${Number(
                                                selectedSpeed
                                            ) ===
                                                Number(
                                                    tab.speed
                                                )
                                                ? "active"
                                                : ""
                                                }`}
                                            onClick={() =>
                                                setSelectedSpeed(
                                                    tab.speed
                                                )
                                            }
                                        >

                                            <Wifi
                                                size={17}
                                            />

                                            <span>
                                                {
                                                    tab.label
                                                }
                                            </span>

                                        </button>

                                    )
                                )}

                            </div>

                        </div>

                    )}

                {/* =====================================================
                    BROADBAND PLAN CARDS
                ====================================================== */}

                {plansLoading ? (

                    <div className="payment-history-empty">

                        <div className="spinner-border spinner-border-sm"></div>

                        <span>
                            Loading broadband plans...
                        </span>

                    </div>

                ) : filteredPlans.length === 0 ? (

                    <div className="payment-history-empty">

                        <Wifi size={30} />

                        <h5>
                            No Broadband Plans Available
                        </h5>

                        <p>
                            Please check again later.
                        </p>

                    </div>

                ) : (

                    <div className="row g-4 justify-content-center">

                        {filteredPlans.map(
                            (plan) => {

                                const durations =
                                    Array.isArray(
                                        plan.durations
                                    )
                                        ? plan.durations
                                        : [];

                                return durations.map(
                                    (duration) => {

                                        const durationOtts =
                                            Array.isArray(
                                                duration.ott_platforms
                                            )
                                                ? duration.ott_platforms
                                                : [];

                                        const isCurrentPlan =
                                            checkIsCurrentPlan(
                                                plan,
                                                duration
                                            );

                                        const pendingPaymentRecord =
                                            !isCurrentPlan
                                                ? getPendingBroadbandPayment(
                                                    plan,
                                                    duration
                                                )
                                                : null;

                                        const isPendingPlan =
                                            Boolean(
                                                pendingPaymentRecord
                                            );

                                        const isUpcomingPlan =
                                            !isCurrentPlan &&
                                            !isPendingPlan &&
                                            checkIsUpcomingPlan(
                                                plan,
                                                duration
                                            );

                                        return (

                                            <div
                                                className="col-lg-4 col-md-6"
                                                key={`${plan.id}-${duration.id}`}
                                            >

                                                <div
                                                    className={`premium-plan-card ${isCurrentPlan
                                                        ? "active-plan-border"
                                                        : isPendingPlan
                                                            ? "pending-plan-border"
                                                            : ""
                                                        }`}
                                                >

                                                    {isCurrentPlan && (

                                                        <div className="active-ribbon">

                                                            <ShieldCheck
                                                                size={12}
                                                            />

                                                            <span>
                                                                Current Active Plan
                                                            </span>

                                                        </div>

                                                    )}

                                                    {isPendingPlan && (

                                                        <div className="active-ribbon pending-plan-ribbon">

                                                            <Clock
                                                                size={12}
                                                            />

                                                            <span>
                                                                Payment Completed - Activation Pending
                                                            </span>

                                                        </div>

                                                    )}

                                                    {isUpcomingPlan && (

                                                        <div className="active-ribbon upcoming-plan-ribbon">

                                                            <Clock
                                                                size={12}
                                                            />

                                                            <span>
                                                                Scheduled Plan
                                                            </span>

                                                        </div>

                                                    )}

                                                    {!isCurrentPlan &&
                                                        !isUpcomingPlan &&
                                                        Number(
                                                            plan.is_popular
                                                        ) === 1 && (

                                                            <div className="active-ribbon">

                                                                Popular

                                                            </div>

                                                        )}

                                                    {isPendingPlan && (

                                                        <div className="plan-pending-note">

                                                            <CheckCircle size={16} />

                                                            <div>

                                                                <strong>
                                                                    Payment completed successfully.
                                                                </strong>

                                                                <span>
                                                                    Your plan activation is pending. It takes some time to activate.
                                                                </span>

                                                            </div>

                                                        </div>

                                                    )}

                                                    {isUpcomingPlan && (

                                                        <div className="plan-continuation-note">

                                                            <CheckCircle size={14} />

                                                            <span>
                                                                This plan is already scheduled to start on{" "}
                                                                <strong>
                                                                    {formatPlanDate(
                                                                        upcomingBroadbandPlan?.plan_start_date
                                                                    )}
                                                                </strong>
                                                                .
                                                            </span>

                                                        </div>

                                                    )}

                                                    {/* PLAN HEADER */}

                                                    <div className="plan-card-top">

                                                        <div className="plan-speed-header">

                                                            <Wifi
                                                                size={24}
                                                                className="plan-wifi-glow"
                                                            />

                                                            <h3>
                                                                {
                                                                    plan.plan_name
                                                                }
                                                            </h3>

                                                        </div>

                                                        <div className="plan-price-block">

                                                            <span className="price-currency">
                                                                ₹
                                                            </span>

                                                            <span className="price-amount">
                                                                {
                                                                    duration.price
                                                                }
                                                            </span>

                                                            <span className="price-duration">

                                                                /

                                                                {" "}

                                                                {
                                                                    duration.duration_label ||
                                                                    duration.label ||
                                                                    `${duration.months} Month${Number(
                                                                        duration.months
                                                                    ) > 1
                                                                        ? "s"
                                                                        : ""
                                                                    }`
                                                                }

                                                            </span>

                                                        </div>

                                                        <p className="plan-tagline">

                                                            {
                                                                plan.tagline ||
                                                                "High-Speed Fiber Unlimited"
                                                            }

                                                        </p>

                                                        {isPendingPlan && (

                                                            <div className="plan-pending-status">

                                                                <Clock size={13} />

                                                                <span>
                                                                    PENDING ACTIVATION
                                                                </span>

                                                            </div>

                                                        )}

                                                    </div>

                                                    <div className="plan-card-divider"></div>

                                                    {/* =================================================
                                                        INCLUDED BROADBAND OTTs
                                                    ================================================== */}

                                                    {durationOtts.length > 0 && (

                                                        <div
                                                            className="included-otts-box interactive"
                                                            onClick={() =>
                                                                setActiveOttModal({
                                                                    plan,
                                                                    duration,
                                                                    planName:
                                                                        plan.plan_name,
                                                                    durationLabel:
                                                                        duration.duration_label ||
                                                                        duration.label ||
                                                                        `${duration.months} Month(s)`,
                                                                    otts:
                                                                        durationOtts
                                                                })
                                                            }
                                                            title="Click to view all included OTT platforms"
                                                        >

                                                            <div className="included-otts-header">

                                                                <span>

                                                                    <Tv
                                                                        size={14}
                                                                        className="inline-icon"
                                                                        style={{
                                                                            marginRight:
                                                                                "6px",
                                                                            verticalAlign:
                                                                                "middle"
                                                                        }}
                                                                    />

                                                                    Included OTTs
                                                                    {" "}
                                                                    (
                                                                    {
                                                                        durationOtts.length
                                                                    }
                                                                    )

                                                                </span>

                                                            </div>

                                                            <div className="included-otts-list">

                                                                {durationOtts
                                                                    .slice(
                                                                        0,
                                                                        3
                                                                    )
                                                                    .map(
                                                                        (ott) => (

                                                                            <div
                                                                                className="ott-logo-wrapper"
                                                                                key={
                                                                                    ott.id
                                                                                }
                                                                                title={
                                                                                    ott.ott_name ||
                                                                                    ott.name
                                                                                }
                                                                            >

                                                                                {ott.logo ? (

                                                                                    <img
                                                                                        src={
                                                                                            ott.logo
                                                                                        }
                                                                                        alt={
                                                                                            ott.ott_name ||
                                                                                            ott.name ||
                                                                                            "OTT"
                                                                                        }
                                                                                        className="ott-logo1"
                                                                                    />

                                                                                ) : (

                                                                                    <span className="ott-logo-fallback">

                                                                                        {
                                                                                            (
                                                                                                ott.ott_name ||
                                                                                                ott.name ||
                                                                                                "OTT"
                                                                                            )
                                                                                                .charAt(
                                                                                                    0
                                                                                                )
                                                                                                .toUpperCase()
                                                                                        }

                                                                                    </span>

                                                                                )}

                                                                            </div>

                                                                        )
                                                                    )}

                                                                {durationOtts.length >
                                                                    3 && (

                                                                        <div className="ott-more-count">

                                                                            +
                                                                            {
                                                                                durationOtts.length -
                                                                                3
                                                                            }

                                                                        </div>

                                                                    )}

                                                            </div>

                                                        </div>

                                                    )}

                                                    {/* BENEFITS */}

                                                    <div className="plan-card-benefits">

                                                        <ul className="benefits-checklist">

                                                            {Array.isArray(
                                                                plan.features
                                                            ) &&
                                                                plan.features.map(
                                                                    (
                                                                        feature,
                                                                        index
                                                                    ) => (

                                                                        <li
                                                                            key={
                                                                                feature.id ||
                                                                                index
                                                                            }
                                                                        >

                                                                            <div className="check-icon-circle">

                                                                                <Check
                                                                                    size={
                                                                                        12
                                                                                    }
                                                                                />

                                                                            </div>

                                                                            <span>

                                                                                {
                                                                                    feature.feature_text
                                                                                }

                                                                            </span>

                                                                        </li>

                                                                    )
                                                                )}

                                                        </ul>

                                                    </div>

                                                    {/* ACTION */}

                                                    <div className="plan-card-action">

                                                        {isCurrentPlan ? (

                                                            <button
                                                                className="btn-renew-premium"
                                                                onClick={() =>
                                                                    goToPayment({
                                                                        plan,
                                                                        duration,
                                                                        continuationMode: true
                                                                    })
                                                                }
                                                            >

                                                                <Zap
                                                                    size={14}
                                                                />

                                                                <span>
                                                                    Buy Another Plan
                                                                </span>

                                                            </button>

                                                        ) : isPendingPlan ? (

                                                            <button
                                                                type="button"
                                                                className="btn-renew-premium pending-plan-action"
                                                                disabled
                                                                title="Payment completed. Waiting for administrator to activate the plan."
                                                            >

                                                                <Clock
                                                                    size={14}
                                                                />

                                                                <span>
                                                                    Activation Pending
                                                                </span>

                                                            </button>

                                                        ) : isUpcomingPlan ? (

                                                            <button
                                                                type="button"
                                                                className="btn-renew-premium continuation-scheduled-button"
                                                                disabled
                                                                title={`This plan is already scheduled to start on ${formatPlanDate(
                                                                    upcomingBroadbandPlan?.plan_start_date
                                                                )}`}
                                                            >

                                                                <CheckCircle
                                                                    size={14}
                                                                />

                                                                <span>
                                                                    Already Scheduled
                                                                </span>

                                                            </button>

                                                        ) : (

                                                            <button
                                                                className="btn-choose-premium"
                                                                onClick={() =>
                                                                    goToPayment({
                                                                        plan,
                                                                        duration,
                                                                        continuationMode: hasActiveBroadbandPlan()
                                                                    })
                                                                }
                                                            >

                                                                <span>
                                                                    {
                                                                        hasActiveBroadbandPlan()
                                                                            ? "Buy Another Plan"
                                                                            : "Choose Plan"
                                                                    }
                                                                </span>

                                                                <ChevronRight
                                                                    size={14}
                                                                />

                                                            </button>

                                                        )}

                                                    </div>

                                                </div>

                                            </div>

                                        );

                                    }
                                );

                            }
                        )}

                    </div>

                )}

                {/* =====================================================
                    OTT ADD-ON PLANS HEADER
                ====================================================== */}

                <div className="plans-section-intro ott-user-intro">

                    <div className="intro-line"></div>

                    <h2>
                        OTT Add-on Plans
                    </h2>

                    <p>
                        Choose an OTT add-on plan that suits your
                        entertainment needs.
                    </p>

                </div>

                {/* =====================================================
                    OTT ADD-ON PLANS
                ====================================================== */}

                {addonLoading ? (

                    <div className="payment-history-empty">

                        <div className="spinner-border spinner-border-sm"></div>

                        <span>
                            Loading OTT add-on plans...
                        </span>

                    </div>

                ) : ottAddonPlans.length === 0 ? (

                    <div className="payment-history-empty">

                        <Activity size={30} />

                        <h5>
                            No OTT Add-on Plans Available
                        </h5>

                        <p>
                            Please check again later.
                        </p>

                    </div>

                ) : (

                    <div className="row g-4 justify-content-center">

                        {ottAddonPlans.map(
                            (addon) => {

                                /*
                                 * IMPORTANT:
                                 *
                                 * The add-on plan ID comes from the
                                 * OTT add-on plans table.
                                 *
                                 * The payment table has TWO different
                                 * IDs:
                                 *
                                 *     payments.id       -> payment row ID
                                 *     addon_plan_id    -> add-on plan ID
                                 *
                                 * Always use addon_plan_id for matching
                                 * the card with its payment.
                                 */
                                const addonPlanId =
                                    getAddonPlanId(
                                        addon
                                    );

                                const addonAssignedOtts =
                                    Array.isArray(
                                        addonOtts[
                                        addonPlanId
                                        ]
                                    )
                                        ? addonOtts[
                                        addonPlanId
                                        ]
                                        : [];

                                /*
                                 * ACTIVE:
                                 *   plan_type = ADDON/OTT_ADDON
                                 *   plan_status = ACTIVE
                                 *   payment_status = SUCCESS
                                 *
                                 * PENDING:
                                 *   plan_type = ADDON/OTT_ADDON
                                 *   plan_status = PENDING
                                 *   payment_status = SUCCESS
                                 */
                                const isCurrentAddon =
                                    isAddonActive(
                                        addon
                                    );

                                const pendingAddonPayment =
                                    !isCurrentAddon
                                        ? getPendingAddonPayment(
                                            addon
                                        )
                                        : null;

                                const isPendingAddon =
                                    Boolean(
                                        pendingAddonPayment
                                    );

                                /*
                                 * Debug information is intentionally
                                 * kept in the console so you can verify
                                 * the exact ID/type/status returned by
                                 * the APIs.
                                 */
                                console.log(
                                    "OTT Add-on Card Status:",
                                    {
                                        addonPlanId,
                                        addonName:
                                            addon.duration_label ||
                                            addon.plan_name,
                                        isCurrentAddon,
                                        isPendingAddon,
                                        paymentPlanType:
                                            pendingAddonPayment?.plan_type,
                                        paymentAddonPlanId:
                                            pendingAddonPayment?.addon_plan_id,
                                        paymentStatus:
                                            pendingAddonPayment?.payment_status,
                                        planStatus:
                                            pendingAddonPayment?.plan_status,
                                        addonPaymentRecordCount:
                                            addonPaymentRecords.length,
                                        addonPaymentStatusLoading
                                    }
                                );

                                return (

                                    <div
                                        className="col-lg-4 col-md-6"
                                        key={
                                            addon.id
                                        }
                                    >

                                        <div
                                            className={`premium-plan-card ${isCurrentAddon
                                                ? "active-plan-border"
                                                : isPendingAddon
                                                    ? "pending-plan-border"
                                                    : ""
                                                }`}
                                            style={
                                                isPendingAddon
                                                    ? {
                                                        border:
                                                            "2px solid #f59e0b",
                                                        boxShadow:
                                                            "0 12px 30px rgba(245, 158, 11, 0.22)",
                                                        background:
                                                            "linear-gradient(180deg, #fffbeb 0%, #ffffff 24%)"
                                                    }
                                                    : undefined
                                            }
                                        >

                                            {/* =================================================
                                                STATUS RIBBON
                                            ================================================== */}

                                            {isCurrentAddon && (

                                                <div className="active-ribbon">

                                                    <ShieldCheck
                                                        size={12}
                                                    />

                                                    <span>
                                                        Current Active Add-on
                                                    </span>

                                                </div>

                                            )}

                                            {isPendingAddon && (

                                                <div
                                                    className="active-ribbon pending-plan-ribbon"
                                                    style={{
                                                        background:
                                                            "linear-gradient(135deg, #f59e0b, #ea580c)",
                                                        color: "#ffffff",
                                                        boxShadow:
                                                            "0 6px 18px rgba(245, 158, 11, 0.30)",
                                                        zIndex: 5
                                                    }}
                                                >

                                                    <Clock
                                                        size={12}
                                                    />

                                                    <span>
                                                        Payment Completed - Activation Pending
                                                    </span>

                                                </div>

                                            )}

                                            {!isCurrentAddon &&
                                                !isPendingAddon &&
                                                Number(
                                                    addon.is_popular
                                                ) === 1 && (

                                                    <div className="active-ribbon">

                                                        Popular

                                                    </div>

                                                )}

                                            {/* =================================================
                                                PENDING NOTICE
                                            ================================================== */}

                                            {isPendingAddon && (

                                                <div className="plan-pending-note">

                                                    <CheckCircle
                                                        size={16}
                                                    />

                                                    <div>

                                                        <strong>
                                                            Payment completed successfully.
                                                        </strong>

                                                        <span>
                                                            Your OTT add-on activation is pending.
                                                            It takes some time to activate.
                                                        </span>

                                                    </div>

                                                </div>

                                            )}

                                            {/* =================================================
                                                PLAN HEADER
                                            ================================================== */}

                                            <div className="plan-card-top">

                                                <div className="plan-speed-header">

                                                    <Zap
                                                        size={24}
                                                        className="plan-wifi-glow"
                                                    />

                                                    <h3>
                                                        {
                                                            addon.duration_label
                                                        }
                                                    </h3>

                                                </div>

                                                <div className="plan-price-block">

                                                    <span className="price-currency">
                                                        ₹
                                                    </span>

                                                    <span className="price-amount">
                                                        {
                                                            addon.price
                                                        }
                                                    </span>

                                                    <span className="price-duration">

                                                        /

                                                        {" "}

                                                        {
                                                            addon.duration_label ||
                                                            `${addon.duration_months} Months`
                                                        }

                                                    </span>

                                                </div>

                                                <p className="plan-tagline">

                                                    {
                                                        addon.description
                                                    }

                                                </p>

                                                {/* SAME STATUS DISPLAY AS BROADBAND */}

                                                {isCurrentAddon && (

                                                    <div className="plan-active-status">

                                                        <ShieldCheck
                                                            size={13}
                                                        />

                                                        <span>
                                                            ACTIVE
                                                        </span>

                                                    </div>

                                                )}

                                                {isPendingAddon && (

                                                    <div className="plan-pending-status">

                                                        <Clock
                                                            size={13}
                                                        />

                                                        <span>
                                                            PENDING ACTIVATION
                                                        </span>

                                                    </div>

                                                )}

                                            </div>

                                            <div className="plan-card-divider"></div>

                                            {/* =================================================
                                                ACTIVE ADD-ON DATES
                                            ================================================== */}

                                            {isCurrentAddon && (

                                                <div className="addon-card-status-details">

                                                    <div>

                                                        <span>
                                                            Activated On
                                                        </span>

                                                        <strong>
                                                            {
                                                                formatPlanDate(
                                                                    (
                                                                        activeAddons.find(
                                                                            (activeAddon) =>
                                                                                getPaymentAddonPlanId(
                                                                                    activeAddon
                                                                                ) ===
                                                                                addonPlanId
                                                                        ) ||
                                                                        getAddonPaymentRecord(
                                                                            addon,
                                                                            ["ACTIVE"]
                                                                        )
                                                                    )?.plan_start_date ||
                                                                    (
                                                                        activeAddons.find(
                                                                            (activeAddon) =>
                                                                                getPaymentAddonPlanId(
                                                                                    activeAddon
                                                                                ) ===
                                                                                addonPlanId
                                                                        ) ||
                                                                        getAddonPaymentRecord(
                                                                            addon,
                                                                            ["ACTIVE"]
                                                                        )
                                                                    )?.start_date
                                                                )
                                                            }
                                                        </strong>

                                                    </div>

                                                    <div>

                                                        <span>
                                                            Expires On
                                                        </span>

                                                        <strong>
                                                            {
                                                                formatPlanDate(
                                                                    (
                                                                        activeAddons.find(
                                                                            (activeAddon) =>
                                                                                getPaymentAddonPlanId(
                                                                                    activeAddon
                                                                                ) ===
                                                                                addonPlanId
                                                                        ) ||
                                                                        getAddonPaymentRecord(
                                                                            addon,
                                                                            ["ACTIVE"]
                                                                        )
                                                                    )?.plan_expiry_date ||
                                                                    (
                                                                        activeAddons.find(
                                                                            (activeAddon) =>
                                                                                getPaymentAddonPlanId(
                                                                                    activeAddon
                                                                                ) ===
                                                                                addonPlanId
                                                                        ) ||
                                                                        getAddonPaymentRecord(
                                                                            addon,
                                                                            ["ACTIVE"]
                                                                        )
                                                                    )?.expiry_date
                                                                )
                                                            }
                                                        </strong>

                                                    </div>

                                                </div>

                                            )}

                                            {/* =================================================
                                                PENDING PAYMENT DETAILS
                                            ================================================== */}

                                            {isPendingAddon && (

                                                <div
                                                    className="addon-card-pending-details"
                                                    style={{
                                                        marginTop: "16px",
                                                        marginBottom: "16px",
                                                        padding: "12px",
                                                        borderRadius: "12px",
                                                        border: "1px solid #fde68a",
                                                        background: "#fffbeb",
                                                        display: "grid",
                                                        gridTemplateColumns: "1fr 1fr",
                                                        gap: "10px"
                                                    }}
                                                >

                                                    <div>

                                                        <span>
                                                            Payment Status
                                                        </span>

                                                        <strong>
                                                            SUCCESS
                                                        </strong>

                                                    </div>

                                                    <div>

                                                        <span>
                                                            Plan Status
                                                        </span>

                                                        <strong>
                                                            PENDING
                                                        </strong>

                                                    </div>

                                                </div>

                                            )}

                                            {/* =================================================
                                                INCLUDED OTTs
                                            ================================================== */}

                                            {addonAssignedOtts.length > 0 && (

                                                <div
                                                    className="included-otts-box interactive"
                                                    onClick={() =>
                                                        setActiveAddonOttModal({
                                                            addon,
                                                            otts:
                                                                addonAssignedOtts
                                                        })
                                                    }
                                                    title="Click to view all included OTT platforms"
                                                >

                                                    <div className="included-otts-header">

                                                        <span>

                                                            <Tv
                                                                size={14}
                                                                className="inline-icon"
                                                                style={{
                                                                    marginRight:
                                                                        "6px",
                                                                    verticalAlign:
                                                                        "middle"
                                                                }}
                                                            />

                                                            Included OTTs
                                                            {" "}
                                                            (
                                                            {
                                                                addonAssignedOtts.length
                                                            }
                                                            )

                                                        </span>

                                                    </div>

                                                    <div className="included-otts-list">

                                                        {addonAssignedOtts
                                                            .slice(
                                                                0,
                                                                3
                                                            )
                                                            .map(
                                                                (ott) => (

                                                                    <div
                                                                        className="ott-logo-wrapper"
                                                                        key={
                                                                            ott.id
                                                                        }
                                                                        title={
                                                                            ott.ott_name ||
                                                                            ott.name ||
                                                                            "OTT"
                                                                        }
                                                                    >

                                                                        {ott.logo ? (

                                                                            <img
                                                                                src={
                                                                                    ott.logo
                                                                                }
                                                                                alt={
                                                                                    ott.ott_name ||
                                                                                    ott.name ||
                                                                                    "OTT"
                                                                                }
                                                                                className="ott-logo1"
                                                                            />

                                                                        ) : (

                                                                            <span className="ott-logo-fallback">

                                                                                {(
                                                                                    ott.ott_name ||
                                                                                    ott.name ||
                                                                                    "OTT"
                                                                                )
                                                                                    .charAt(
                                                                                        0
                                                                                    )
                                                                                    .toUpperCase()}

                                                                            </span>

                                                                        )}

                                                                    </div>

                                                                )
                                                            )}

                                                        {addonAssignedOtts.length >
                                                            3 && (

                                                                <div className="ott-more-count">

                                                                    +
                                                                    {
                                                                        addonAssignedOtts.length -
                                                                        3
                                                                    }

                                                                </div>

                                                            )}

                                                    </div>

                                                </div>

                                            )}

                                            {/* =================================================
                                                BENEFITS
                                            ================================================== */}

                                            <div className="plan-card-benefits">

                                                <ul className="benefits-checklist">

                                                    {Array.isArray(
                                                        addon.features
                                                    ) &&
                                                        addon.features.map(
                                                            (
                                                                feature,
                                                                index
                                                            ) => (

                                                                <li
                                                                    key={
                                                                        feature.id ||
                                                                        index
                                                                    }
                                                                >

                                                                    <div className="check-icon-circle">

                                                                        <Check
                                                                            size={
                                                                                12
                                                                            }
                                                                        />

                                                                    </div>

                                                                    <span>

                                                                        {
                                                                            feature.feature_text
                                                                        }

                                                                    </span>

                                                                </li>

                                                            )
                                                        )}

                                                </ul>

                                            </div>

                                            {/* =================================================
                                                ACTION
                                            ================================================== */}

                                            <div className="plan-card-action">

                                                {isCurrentAddon ? (

                                                    <button
                                                        type="button"
                                                        className="btn-renew-premium"
                                                        disabled
                                                        title="This OTT add-on is already active"
                                                    >

                                                        <CheckCircle
                                                            size={14}
                                                        />

                                                        <span>
                                                            Currently Active
                                                        </span>

                                                    </button>

                                                ) : isPendingAddon ? (

                                                    <button
                                                        type="button"
                                                        className="btn-renew-premium pending-plan-action"
                                                        disabled
                                                        title="Payment completed. Waiting for administrator to activate the OTT add-on."
                                                    >

                                                        <Clock
                                                            size={14}
                                                        />

                                                        <span>
                                                            Activation Pending
                                                        </span>

                                                    </button>

                                                ) : (

                                                    <button
                                                        type="button"
                                                        className={`btn-choose-premium ${!hasActiveBroadbandPlan()
                                                            ? "addon-disabled-button"
                                                            : ""
                                                            }`}
                                                        aria-disabled={
                                                            !hasActiveBroadbandPlan()
                                                        }
                                                        title={
                                                            !hasActiveBroadbandPlan()
                                                                ? "Please buy an active broadband plan first"
                                                                : "Choose OTT Add-on"
                                                        }
                                                        onClick={() =>
                                                            chooseAddon(
                                                                addon
                                                            )
                                                        }
                                                    >

                                                        <span>
                                                            Choose Add-on
                                                        </span>

                                                        <ChevronRight
                                                            size={14}
                                                        />

                                                    </button>

                                                )}

                                            </div>

                                        </div>

                                    </div>

                                );

                            }
                        )}

                    </div>

                )}

                {/* =====================================================
                    PAYMENT HISTORY
                ====================================================== */}

                <div className="payment-history-widget">

                    <div className="payment-history-header">

                        <div>

                            <div className="payment-history-title">

                                <div className="payment-history-icon">

                                    <Activity
                                        size={19}
                                    />

                                </div>

                                <div>

                                    <h4>
                                        Payment History
                                    </h4>

                                    <p>
                                        Your previous subscription
                                        payments and transactions
                                    </p>

                                </div>

                            </div>

                        </div>

                        <div className="payment-history-filters">

                            <div className="payment-search-box">

                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search payment..."
                                    value={
                                        paymentSearch
                                    }
                                    onChange={(e) => {

                                        setPaymentSearch(
                                            e.target.value
                                        );

                                        setPaymentPage(
                                            1
                                        );

                                    }}
                                />

                            </div>

                            <div className="payment-month-filter">

                                <label>
                                    Month
                                </label>

                                <input
                                    type="month"
                                    className="form-control"
                                    value={
                                        paymentMonth
                                    }
                                    onChange={(e) => {

                                        setPaymentMonth(
                                            e.target.value
                                        );

                                        setPaymentPage(
                                            1
                                        );

                                    }}
                                />

                            </div>

                            <button
                                type="button"
                                className="payment-clear-btn"
                                onClick={() => {

                                    setPaymentSearch(
                                        ""
                                    );

                                    setPaymentMonth(
                                        ""
                                    );

                                    setPaymentPage(
                                        1
                                    );

                                }}
                            >
                                Clear
                            </button>

                        </div>

                        <span className="payment-count-badge">

                            {
                                paymentTotal
                            }

                            {" "}

                            Transaction
                            {
                                paymentTotal !==
                                    1
                                    ? "s"
                                    : ""
                            }

                        </span>

                    </div>

                    <div className="payment-history-body">

                        {paymentHistoryLoading ? (

                            <div className="payment-history-empty">

                                <div className="spinner-border spinner-border-sm"></div>

                                <span>
                                    Loading payment history...
                                </span>

                            </div>

                        ) : paymentHistory.length ===
                            0 ? (

                            <div className="payment-history-empty">

                                <Activity
                                    size={30}
                                />

                                <h5>
                                    No Payment History
                                </h5>

                                <p>
                                    You have not made any
                                    payments yet.
                                </p>

                            </div>

                        ) : (

                            <div className="table-responsive">

                                <table className="table payment-history-table">

                                    <thead>

                                        <tr>

                                            <th>
                                                #
                                            </th>

                                            <th>
                                                Plan
                                            </th>

                                            <th>
                                                Amount
                                            </th>

                                            <th>
                                                Payment Method
                                            </th>

                                            <th>
                                                Payment Date
                                            </th>

                                            <th>
                                                Validity
                                            </th>

                                            <th>
                                                Status
                                            </th>

                                        </tr>

                                    </thead>

                                    <tbody>

                                        {paymentHistory.map(
                                            (
                                                payment,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        payment.id ||
                                                        index
                                                    }
                                                >

                                                    <td>

                                                        <span className="payment-index">

                                                            {
                                                                index +
                                                                1
                                                            }

                                                        </span>

                                                    </td>

                                                    <td>

                                                        <div className="payment-plan-info">

                                                            <Wifi
                                                                size={
                                                                    15
                                                                }
                                                            />

                                                            <strong>

                                                                {
                                                                    payment.plan_name
                                                                }

                                                            </strong>

                                                        </div>

                                                    </td>

                                                    <td>

                                                        <strong className="payment-amount">

                                                            ₹
                                                            {
                                                                payment.amount
                                                            }

                                                        </strong>

                                                    </td>

                                                    <td>

                                                        <span className="payment-method-badge">

                                                            {
                                                                payment.payment_method ||
                                                                "ONLINE"
                                                            }

                                                        </span>

                                                    </td>

                                                    <td>

                                                        <div className="payment-date">

                                                            <Calendar
                                                                size={
                                                                    13
                                                                }
                                                            />

                                                            <span>

                                                                {
                                                                    formatIndiaDateTime(
                                                                        payment.transaction_date
                                                                    )
                                                                }

                                                            </span>

                                                        </div>

                                                    </td>

                                                    <td>

                                                        {String(
                                                            payment.plan_status || ""
                                                        ).trim().toUpperCase() === "PENDING" ? (

                                                            <span
                                                                style={{
                                                                    fontSize: "12px",
                                                                    opacity: 0.75
                                                                }}
                                                            >
                                                                Awaiting admin activation
                                                            </span>

                                                        ) : (

                                                            <div className="payment-validity">

                                                                <span>
                                                                    {
                                                                        formatPlanDate(
                                                                            payment.plan_start_date
                                                                        )
                                                                    }
                                                                </span>

                                                                <span className="validity-arrow">
                                                                    →
                                                                </span>

                                                                <span>
                                                                    {
                                                                        formatPlanDate(
                                                                            payment.plan_expiry_date
                                                                        )
                                                                    }
                                                                </span>

                                                            </div>

                                                        )}

                                                    </td>

                                                    <td>

                                                        <span
                                                            className={`payment-status ${String(
                                                                payment.payment_status || ""
                                                            ).trim().toUpperCase() === "SUCCESS"
                                                                ? "payment-success"
                                                                : String(
                                                                    payment.payment_status || ""
                                                                ).trim().toUpperCase() === "PENDING"
                                                                    ? "payment-pending"
                                                                    : "payment-failed"
                                                                }`}
                                                        >

                                                            <span className="status-dot"></span>

                                                            {
                                                                payment.payment_status ||
                                                                "UNKNOWN"
                                                            }

                                                            {String(
                                                                payment.plan_status || ""
                                                            ).trim().toUpperCase() === "PENDING" &&
                                                                String(
                                                                    payment.payment_status || ""
                                                                ).trim().toUpperCase() === "SUCCESS" && (
                                                                    <small
                                                                        style={{
                                                                            display: "block",
                                                                            marginTop: "3px",
                                                                            fontSize: "10px",
                                                                            opacity: 0.8
                                                                        }}
                                                                    >
                                                                        Awaiting activation
                                                                    </small>
                                                                )}

                                                        </span>

                                                    </td>

                                                </tr>

                                            )
                                        )}

                                    </tbody>

                                </table>

                                {/* PAGINATION */}

                                {paymentTotalPages >
                                    1 && (

                                        <div className="payment-pagination">

                                            <button
                                                type="button"
                                                className="pagination-btn"
                                                disabled={
                                                    paymentPage ===
                                                    1
                                                }
                                                onClick={() =>
                                                    setPaymentPage(
                                                        paymentPage -
                                                        1
                                                    )
                                                }
                                            >
                                                Previous
                                            </button>

                                            <div className="pagination-pages">

                                                {Array.from(
                                                    {
                                                        length:
                                                            paymentTotalPages
                                                    },
                                                    (
                                                        _,
                                                        index
                                                    ) =>
                                                        index +
                                                        1
                                                ).map(
                                                    (
                                                        page
                                                    ) => (

                                                        <button
                                                            key={
                                                                page
                                                            }
                                                            type="button"
                                                            className={`pagination-number ${paymentPage ===
                                                                page
                                                                ? "active"
                                                                : ""
                                                                }`}
                                                            onClick={() =>
                                                                setPaymentPage(
                                                                    page
                                                                )
                                                            }
                                                        >
                                                            {
                                                                page
                                                            }
                                                        </button>

                                                    )
                                                )}

                                            </div>

                                            <button
                                                type="button"
                                                className="pagination-btn"
                                                disabled={
                                                    paymentPage ===
                                                    paymentTotalPages
                                                }
                                                onClick={() =>
                                                    setPaymentPage(
                                                        paymentPage +
                                                        1
                                                    )
                                                }
                                            >
                                                Next
                                            </button>

                                        </div>

                                    )}

                            </div>

                        )}

                    </div>

                </div>

            </div>

            {/* =========================================================
                BROADBAND OTT DETAIL POPUP
            ========================================================= */}

            {activeOttModal && (

                <div
                    className="ott-modal-overlay"
                    onClick={() =>
                        setActiveOttModal(null)
                    }
                >

                    <div
                        className="ott-modal-card"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <button
                            className="ott-modal-close"
                            onClick={() =>
                                setActiveOttModal(null)
                            }
                        >

                            <X size={20} />

                        </button>

                        <div className="ott-modal-header">

                            <div className="ott-modal-badge">

                                <Tv size={20} />

                            </div>

                            <div>

                                <h3 className="ott-modal-title">

                                    {
                                        activeOttModal.planName
                                    }

                                    {" "}Plan

                                </h3>

                                <p className="ott-modal-sub">

                                    Included OTT Apps &amp;
                                    Subscriptions
                                    {" "}
                                    (
                                    {
                                        activeOttModal.otts.length
                                    }
                                    )
                                    {" "}for{" "}
                                    {
                                        activeOttModal.durationLabel
                                    }

                                </p>

                            </div>

                        </div>

                        <div className="ott-modal-body">

                            {activeOttModal.otts.length >
                                0 ? (

                                <div className="ott-modal-grid">

                                    {activeOttModal.otts.map(
                                        (ott) => (

                                            <div
                                                key={
                                                    ott.id
                                                }
                                                className="ott-modal-item"
                                            >

                                                {ott.logo ? (

                                                    <img
                                                        src={
                                                            ott.logo
                                                        }
                                                        alt={
                                                            ott.ott_name ||
                                                            ott.name
                                                        }
                                                        className="ott-modal-img"
                                                    />

                                                ) : (

                                                    <div className="ott-modal-fallback">

                                                        {(
                                                            ott.ott_name ||
                                                            ott.name ||
                                                            "OTT"
                                                        )
                                                            .charAt(
                                                                0
                                                            )
                                                            .toUpperCase()}

                                                    </div>

                                                )}

                                                <div className="ott-modal-info">

                                                    <span className="ott-modal-name">

                                                        {
                                                            ott.ott_name ||
                                                            ott.name
                                                        }

                                                    </span>

                                                    <span className="ott-modal-tag">

                                                        <CheckCircle
                                                            size={
                                                                12
                                                            }
                                                        />

                                                        Included

                                                    </span>

                                                </div>

                                            </div>

                                        )
                                    )}

                                </div>

                            ) : (

                                <p className="ott-modal-empty">

                                    No OTT apps currently
                                    assigned to this plan.

                                </p>

                            )}

                        </div>

                        <div className="ott-modal-footer">

                            <button
                                className="btn-choose-premium w-100"
                                style={{
                                    height: "44px"
                                }}
                                onClick={() => {

                                    const selectPlan =
                                        activeOttModal.plan;

                                    const selectDuration =
                                        activeOttModal.duration;

                                    setActiveOttModal(
                                        null
                                    );

                                    goToPayment({
                                        plan:
                                            selectPlan,

                                        duration:
                                            selectDuration
                                    });

                                }}
                            >

                                Choose Plan &amp; Continue

                            </button>

                        </div>

                    </div>

                </div>

            )}

            {/* =========================================================
                ADD-ON OTT DETAIL POPUP
            ========================================================= */}

            {activeAddonOttModal && (

                <div
                    className="ott-modal-overlay"
                    onClick={() =>
                        setActiveAddonOttModal(null)
                    }
                >

                    <div
                        className="ott-modal-card"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <button
                            className="ott-modal-close"
                            onClick={() =>
                                setActiveAddonOttModal(null)
                            }
                        >

                            <X size={20} />

                        </button>

                        <div className="ott-modal-header">

                            <div className="ott-modal-badge">

                                <Tv size={20} />

                            </div>

                            <div>

                                <h3 className="ott-modal-title">

                                    {
                                        activeAddonOttModal
                                            .addon
                                            .duration_label
                                    }

                                    {" "}OTT Add-on

                                </h3>

                                <p className="ott-modal-sub">

                                    Included OTT Apps &amp;
                                    Subscriptions
                                    {" "}
                                    (
                                    {
                                        activeAddonOttModal
                                            .otts
                                            .length
                                    }
                                    )

                                </p>

                            </div>

                        </div>

                        <div className="ott-modal-body">

                            {activeAddonOttModal.otts.length >
                                0 ? (

                                <div className="ott-modal-grid">

                                    {activeAddonOttModal.otts.map(
                                        (ott) => (

                                            <div
                                                key={
                                                    ott.id
                                                }
                                                className="ott-modal-item"
                                            >

                                                {ott.logo ? (

                                                    <img
                                                        src={
                                                            ott.logo
                                                        }
                                                        alt={
                                                            ott.ott_name ||
                                                            ott.name ||
                                                            "OTT"
                                                        }
                                                        className="ott-modal-img"
                                                    />

                                                ) : (

                                                    <div className="ott-modal-fallback">

                                                        {(
                                                            ott.ott_name ||
                                                            ott.name ||
                                                            "OTT"
                                                        )
                                                            .charAt(
                                                                0
                                                            )
                                                            .toUpperCase()}

                                                    </div>

                                                )}

                                                <div className="ott-modal-info">

                                                    <span className="ott-modal-name">

                                                        {
                                                            ott.ott_name ||
                                                            ott.name
                                                        }

                                                    </span>

                                                    <span className="ott-modal-tag">

                                                        <CheckCircle
                                                            size={
                                                                12
                                                            }
                                                        />

                                                        Included

                                                    </span>

                                                </div>

                                            </div>

                                        )
                                    )}

                                </div>

                            ) : (

                                <p className="ott-modal-empty">

                                    No OTT apps currently
                                    assigned to this add-on plan.

                                </p>

                            )}

                        </div>

                        <div className="ott-modal-footer">

                            <button
                                className="btn-choose-premium w-100"
                                style={{
                                    height: "44px"
                                }}
                                onClick={() => {

                                    const addon =
                                        activeAddonOttModal.addon;

                                    setActiveAddonOttModal(
                                        null
                                    );

                                    goToPayment({
                                        plan:
                                            addon,

                                        duration: {

                                            months:
                                                addon.duration_months,

                                            duration_months:
                                                addon.duration_months,

                                            duration_label:
                                                addon.duration_label,

                                            price:
                                                addon.price

                                        },

                                        planType:
                                            "OTT_ADDON",

                                        addonPlan:
                                            addon,

                                        selectedAddon:
                                            addon,

                                        hasActiveBroadband:
                                            true

                                    });

                                }}
                            >

                                Choose Add-on &amp; Continue

                            </button>

                        </div>

                    </div>

                </div>

            )}

            {/* =========================================================
                PAYMENT CONFIRMATION
                Shown before any new payment when an active broadband
                plan already exists.

                IMPORTANT:
                The existence of a successful PENDING payment does not
                make this condition true. Only getCurrentPlan.php ACTIVE
                records are treated as active broadband plans.

                The content changes based on pendingPayment.planType:
                - OTT_ADDON -> OTT add-on confirmation
                - otherwise -> broadband plan confirmation
            ========================================================= */}

            {pendingPayment && hasActiveBroadbandPlan() && (

                <div
                    className="ott-modal-overlay"
                    onClick={cancelPendingPayment}
                    style={{
                        zIndex: 9999
                    }}
                >

                    <div
                        className="ott-modal-card"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                        style={{
                            maxWidth: "520px"
                        }}
                    >

                        <button
                            type="button"
                            className="ott-modal-close"
                            onClick={cancelPendingPayment}
                            aria-label="Close confirmation"
                        >

                            <X size={20} />

                        </button>

                        {/* =====================================================
                            HEADER
                        ====================================================== */}

                        <div className="ott-modal-header">

                            <div
                                className="ott-modal-badge"
                                style={{
                                    background:
                                        "rgba(245, 158, 11, 0.12)"
                                }}
                            >

                                <ShieldCheck size={20} />

                            </div>

                            <div>

                                <h3 className="ott-modal-title">

                                    {pendingPayment?.planType ===
                                        "OTT_ADDON"
                                        ? "Active Broadband Plan Exists"
                                        : "Active Plan Already Exists"}

                                </h3>

                                <p className="ott-modal-sub">
                                    Please confirm before continuing to payment.
                                </p>

                            </div>

                        </div>

                        {/* =====================================================
                            BODY
                        ====================================================== */}

                        <div
                            className="ott-modal-body"
                            style={{
                                paddingTop: "10px"
                            }}
                        >

                            <div
                                style={{
                                    padding: "18px",
                                    borderRadius: "12px",
                                    background:
                                        "rgba(245, 158, 11, 0.08)",
                                    border:
                                        "1px solid rgba(245, 158, 11, 0.20)"
                                }}
                            >

                                {/* =================================================
                                    OTT ADD-ON CONFIRMATION
                                ================================================== */}

                                {pendingPayment?.planType ===
                                    "OTT_ADDON" ? (

                                    <>

                                        <p
                                            style={{
                                                margin: 0,
                                                lineHeight: 1.6
                                            }}
                                        >

                                            You already have an active broadband
                                            plan{" "}
                                            <strong>
                                                {
                                                    currentBroadbandPlan?.plan_name ||
                                                    "plan"
                                                }
                                            </strong>
                                            {" "}that expires on{" "}
                                            <strong>
                                                {formatPlanDate(
                                                    currentBroadbandPlan?.plan_expiry_date
                                                )}
                                            </strong>
                                            .

                                        </p>

                                        <p
                                            style={{
                                                margin: "12px 0 0",
                                                lineHeight: 1.6
                                            }}
                                        >

                                            You are purchasing the OTT add-on{" "}
                                            <strong>
                                                {
                                                    pendingPayment
                                                        ?.addonPlan
                                                        ?.duration_label ||
                                                    pendingPayment
                                                        ?.selectedAddon
                                                        ?.duration_label ||
                                                    pendingPayment
                                                        ?.plan
                                                        ?.duration_label ||
                                                    "OTT Add-on"
                                                }
                                            </strong>
                                            .

                                        </p>

                                        <p
                                            style={{
                                                margin: "12px 0 0",
                                                lineHeight: 1.6
                                            }}
                                        >

                                            If you continue, only the OTT add-on
                                            payment will be processed. It is
                                            separate from your broadband plan
                                            and will follow the add-on activation
                                            status returned by the payment system.

                                        </p>

                                        <p
                                            style={{
                                                margin: "12px 0 0",
                                                lineHeight: 1.6
                                            }}
                                        >

                                            Add-on Price:{" "}
                                            <strong>
                                                ₹
                                                {Number(
                                                    pendingPayment
                                                        ?.addonPlan
                                                        ?.price ||
                                                    pendingPayment
                                                        ?.selectedAddon
                                                        ?.price ||
                                                    pendingPayment
                                                        ?.duration
                                                        ?.price ||
                                                    0
                                                ).toFixed(2)}
                                            </strong>

                                        </p>

                                    </>

                                ) : (

                                    /* =================================================
                                       BROADBAND PLAN CONFIRMATION
                                    ================================================== */

                                    <>

                                        <p
                                            style={{
                                                margin: 0,
                                                lineHeight: 1.6
                                            }}
                                        >

                                            You already have an active broadband
                                            plan{" "}
                                            <strong>
                                                {
                                                    currentBroadbandPlan?.plan_name ||
                                                    "plan"
                                                }
                                            </strong>
                                            {" "}that expires on{" "}
                                            <strong>
                                                {formatPlanDate(
                                                    currentBroadbandPlan?.plan_expiry_date
                                                )}
                                            </strong>
                                            .

                                        </p>

                                        <p
                                            style={{
                                                margin: "12px 0 0",
                                                lineHeight: 1.6
                                            }}
                                        >

                                            If you continue, your new broadband
                                            payment will be processed. After a
                                            successful payment, the plan will
                                            remain PENDING until an administrator
                                            manually activates it.

                                        </p>

                                        {pendingPayment?.plan?.plan_name && (

                                            <p
                                                style={{
                                                    margin: "12px 0 0",
                                                    lineHeight: 1.6
                                                }}
                                            >

                                                Selected plan:{" "}
                                                <strong>
                                                    {
                                                        pendingPayment
                                                            .plan
                                                            .plan_name
                                                    }
                                                </strong>

                                            </p>

                                        )}

                                    </>

                                )}

                            </div>

                        </div>

                        {/* =====================================================
                            FOOTER
                        ====================================================== */}

                        <div
                            className="ott-modal-footer"
                            style={{
                                display: "flex",
                                gap: "10px"
                            }}
                        >

                            <button
                                type="button"
                                className="btn-choose-premium w-100"
                                style={{
                                    height: "44px"
                                }}
                                onClick={cancelPendingPayment}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="btn-renew-premium w-100"
                                style={{
                                    height: "44px"
                                }}
                                onClick={confirmPendingPayment}
                            >
                                Continue to Payment
                            </button>

                        </div>

                    </div>

                </div>

            )}

            {/* SCHEDULED PLAN WARNING MODAL */}
            {showScheduledWarning && (
                <div
                    className="edit-user-overlay"
                    onClick={() => setShowScheduledWarning(false)}
                >
                    <div
                        className="scheduled-warning-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Icon */}
                        <div className="scheduled-warning-icon">
                            <Clock size={32} />
                        </div>

                        {/* Title */}
                        <h3 className="scheduled-warning-title">
                            Scheduled Plan Already Active
                        </h3>

                        {/* Body */}
                        <p className="scheduled-warning-body">
                            You already have a broadband plan{" "}
                            <strong>
                                {upcomingBroadbandPlan?.plan_name}
                            </strong>{" "}
                            scheduled to start on{" "}
                            <strong>
                                {formatPlanDate(upcomingBroadbandPlan?.plan_start_date)}
                            </strong>
                            .
                        </p>
                        <p className="scheduled-warning-body" style={{ marginTop: 10 }}>
                            Once your scheduled plan becomes <strong>active</strong>,
                            you can purchase a new plan.
                        </p>

                        {/* Action */}
                        <button
                            type="button"
                            className="scheduled-warning-btn"
                            onClick={() => setShowScheduledWarning(false)}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}

            {/* EDIT USER MODAL */}
            {showEditModal && (
                <div className="edit-user-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="edit-user-modal" onClick={e => e.stopPropagation()}>
                        <div className="edit-user-header">
                            <div>
                                <h2>Edit Profile</h2>
                                <p>Update your personal information.</p>
                            </div>
                            <button
                                type="button"
                                className="edit-user-close"
                                onClick={() => setShowEditModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateUser} className="edit-user-form">
                            <div className="edit-form-group">
                                <label>Full Name *</label>
                                <input
                                    type="text"
                                    name="fullname"
                                    value={editForm.fullname}
                                    onChange={handleEditChange}
                                    required
                                />
                            </div>

                            <div className="edit-form-group">
                                <label>Username</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={editForm.username}
                                    disabled={true}
                                />
                                <small>Username cannot be changed.</small>
                            </div>

                            <div className="edit-form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={editForm.email}
                                    disabled={true}
                                />
                                <small>Email cannot be changed.</small>
                            </div>

                            <div className="edit-form-group">
                                <label>Mobile</label>
                                <input
                                    type="text"
                                    name="mobile"
                                    value={editForm.mobile}
                                    disabled={true}
                                />
                                <small>Mobile cannot be changed.</small>
                            </div>

                            <div className="edit-form-group">
                                <label>Password (Optional)</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={editForm.password}
                                        onChange={handleEditChange}
                                        placeholder="Leave blank to keep current"
                                        style={{ paddingRight: '40px' }}
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#64748b',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: 0
                                        }}
                                        title={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <div className="edit-form-group">
                                <label>City</label>
                                <input
                                    type="text"
                                    name="city"
                                    value={editForm.city}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className="edit-form-group" style={{ gridColumn: '1 / -1' }}>
                                <label>Address</label>
                                <textarea
                                    name="address"
                                    rows="3"
                                    value={editForm.address}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className="edit-form-group">
                                <label>Pincode</label>
                                <input
                                    type="text"
                                    name="pincode"
                                    value={editForm.pincode}
                                    onChange={handleEditChange}
                                />
                            </div>

                            <div className="edit-user-actions">
                                <button
                                    type="button"
                                    className="edit-cancel-btn"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={updatingUser}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="edit-save-btn"
                                    disabled={updatingUser}
                                >
                                    {updatingUser ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>

    );
}

export default UserDashboard;